import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Table2,
  BarChart3,
  LineChart,
  Download,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  ExternalLink,
  AlertTriangle,
  Brain,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart as RLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#4A90D9", "#E5634D", "#50C878", "#FFB347", "#9B59B6", "#1ABC9C", "#E67E22", "#3498DB"];

/* ─── Shared helpers ─── */

function NcDisplay({ nc, label }) {
  if (nc === null || nc === undefined) return <span className="text-[#6E7787]">—</span>;
  const isGain = label === "Gain";
  const isLoss = label === "Loss";
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-body font-medium ${isGain ? "text-emerald-400" : isLoss ? "text-red-400" : "text-[#6E7787]"}`}>
      {isGain ? <TrendingUp className="h-3.5 w-3.5" /> : isLoss ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
      {nc >= 0 ? "+" : ""}{(nc * 100).toFixed(1)}%
      <span className="text-xs opacity-70">({label})</span>
    </span>
  );
}

function ChartTooltipContent({ active, payload, label: tooltipLabel }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#11141A] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-white font-body font-medium mb-1">{tooltipLabel}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-body" style={{ color: p.color }}>{p.name}: {typeof p.value === "number" ? p.value.toFixed(2) : p.value}</p>
      ))}
    </div>
  );
}

function exportCsv(rows, filename = "export.csv") {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/** Styled toggle group for filters */
function FilterToggle({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-1 p-0.5 rounded-md" style={{ backgroundColor: "var(--admin-bg)" }}>
      {options.map(({ v, l }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${v}` : undefined}
          className="px-2.5 py-1 rounded text-[10px] font-body transition-colors"
          style={{
            backgroundColor: value === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
            color: "var(--admin-container-text)",
            opacity: value === v ? 1 : 0.5,
            fontWeight: value === v ? 600 : 400,
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function FlagBadge({ flag }) {
  const colors = {
    "Too Easy": "bg-amber-500/20 text-amber-400 border-amber-500/30",
    "Too Hard": "bg-red-500/20 text-red-400 border-red-500/30",
    "Poor Discriminator": "bg-orange-500/20 text-orange-400 border-orange-500/30",
    "Review Required": "bg-rose-600/20 text-rose-400 border-rose-600/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-body font-semibold border ${colors[flag] || "bg-white/10 text-white border-white/10"}`}>
      <AlertTriangle className="h-2.5 w-2.5" />{flag}
    </span>
  );
}

/* ─── Multi-select checkbox list ─── */
function CheckboxList({ items, selected, toggle, labelKey = "name", valueKey = "id", testIdPrefix = "" }) {
  return (
    <div className="rounded-lg p-2 max-h-32 overflow-y-auto space-y-1" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)" }}>
      {items.map((item) => {
        const val = typeof item === "string" ? item : item[valueKey];
        const label = typeof item === "string" ? item : item[labelKey];
        return (
          <label key={val} className="flex items-center gap-2 text-xs font-body cursor-pointer" style={{ color: "var(--admin-text)" }}>
            <input type="checkbox" checked={selected.includes(val)} onChange={() => toggle(val)} className="rounded" style={{ accentColor: "var(--primary-color, #4A90D9)" }} data-testid={testIdPrefix ? `${testIdPrefix}-${val}` : undefined} />
            {label}
          </label>
        );
      })}
      {items.length === 0 && <p className="text-xs italic" style={{ color: "var(--admin-container-muted)" }}>None available</p>}
    </div>
  );
}

function toggleIn(arr, val) { return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]; }

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════ */

export default function Analytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [layer, setLayer] = useState("comparison");

  /* ─── Tab 1: Cohort Comparison ─── */
  const [cohortData, setCohortData] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [yAxisFit, setYAxisFit] = useState(false); // false=0-100, true=zoom to data
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  // Phase 6 cohort-comparison filter additions:
  // - selectedScoringObjectTypes: which kinds of scoring objects appear in
  //   the Specific Items panel ("track" | "lexp" | "historical_assessment").
  // - selectedSpecificItems: list of {kind, id} chosen from the Specific
  //   Items panel; drives which columns the comparison table renders.
  const [selectedScoringObjectTypes, setSelectedScoringObjectTypes] = useState([]);
  const [selectedSpecificItems, setSelectedSpecificItems] = useState([]);
  const [excludedLearners, setExcludedLearners] = useState({}); // { cohort_id: [learner_ids] }
  const [learnerPanelCohort, setLearnerPanelCohort] = useState("");
  const [cohortRosters, setCohortRosters] = useState({}); // { cohort_id: [{id, first_name, last_name}] }
  const [showFilters, setShowFilters] = useState(false);
  const [ncPairings, setNcPairings] = useState({});
  const [ncResults, setNcResults] = useState({});
  const [compMetric, setCompMetric] = useState("mean");
  const [cohortStatusFilter, setCohortStatusFilter] = useState("active");

  /* ─── Comparative Analytics: Learner view ─── */
  const [compView, setCompView] = useState("cohort");
  const [learnerData, setLearnerData] = useState({ assessment_types: [], learners: [] });
  const [learnerLoading, setLearnerLoading] = useState(false);
  const [learnerSortField, setLearnerSortField] = useState(null);
  const [learnerSortDir, setLearnerSortDir] = useState("asc");
  const [learnerPreType, setLearnerPreType] = useState("");
  const [learnerPostType, setLearnerPostType] = useState("");
  const [learnerStatusFilter, setLearnerStatusFilter] = useState("active");
  const [cohortTypeFilter, setCohortTypeFilter] = useState("all");
  const [showMobileChart, setShowMobileChart] = useState(false);
  const [learnerSearch, setLearnerSearch] = useState("");

  /* ─── Tab 3: Psychometrics ─── */
  const [psyCohortIds, setPsyCohortIds] = useState([]);
  const [psyTypeIds, setPsyTypeIds] = useState([]);
  const [psyTopicTags, setPsyTopicTags] = useState([]);
  const [psyItemType, setPsyItemType] = useState("");
  const [psyResults, setPsyResults] = useState([]);
  const [psyLoading, setPsyLoading] = useState(false);
  const [psyView, setPsyView] = useState("table");
  const [psyFilterOptions, setPsyFilterOptions] = useState({ topic_tags: [], meta_tags: [], item_types: [] });
  const [psySortField, setPsySortField] = useState(null);
  const [psySortDir, setPsySortDir] = useState("asc");
  const [expandedItem, setExpandedItem] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  /* ─── Tab 4: Survey Data ─── */
  const [surveyData, setSurveyData] = useState({ survey_items: [], cohorts: [], learners: [], assessment_types: [] });
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyView, setSurveyView] = useState("cohort");
  const [surveyViewMode, setSurveyViewMode] = useState("table");
  const [surveySortField, setSurveySortField] = useState(null);
  const [surveySortDir, setSurveySortDir] = useState("asc");
  const [surveySearch, setSurveySearch] = useState("");
  const [surveyShowFilters, setSurveyShowFilters] = useState(false);
  const [surveyCohortStatus, setSurveyCohortStatus] = useState("active");
  const [surveySelectedCohorts, setSurveySelectedCohorts] = useState([]);
  const [surveyLearnerStatus, setSurveyLearnerStatus] = useState("active");
  const [surveySelectedAssessments, setSurveySelectedAssessments] = useState([]);

  /* ─── Tab: Telemetry Analytics ─── */
  const [telData, setTelData] = useState({ cohorts: [], tracks: [], learners: [], telemetry: [], track_lexp_map: {}, cohort_tracks: {} });
  const [telLoading, setTelLoading] = useState(false);
  const [telView, setTelView] = useState("cohort"); // "cohort" | "learner"
  const [telCohortStatus, setTelCohortStatus] = useState("active");
  const [telSelectedCohorts, setTelSelectedCohorts] = useState([]);
  const [telSelectedTrack, setTelSelectedTrack] = useState("all");
  const [telSortField, setTelSortField] = useState("name");
  const [telSortDir, setTelSortDir] = useState("asc");
  const [telSearch, setTelSearch] = useState("");
  const [telShowFilters, setTelShowFilters] = useState(false);
  const [telExpandedCohorts, setTelExpandedCohorts] = useState({});

  /* ─── Data fetching ─── */
  const fetchCohorts = useCallback(async () => {
    try {
      const hasExclusions = Object.values(excludedLearners).some((arr) => arr.length > 0);
      if (hasExclusions) {
        const body = { excluded_learners: excludedLearners };
        if (cohortStatusFilter !== "all") body.cohort_status = cohortStatusFilter;
        if (cohortTypeFilter !== "all") body.cohort_type = cohortTypeFilter;
        const { data } = await axios.post(`${API}/analytics/cohorts`, body, { withCredentials: true });
        setCohortData(data);
      } else {
        const params = {};
        if (cohortStatusFilter !== "all") params.cohort_status = cohortStatusFilter;
        if (cohortTypeFilter !== "all") params.cohort_type = cohortTypeFilter;
        const { data } = await axios.get(`${API}/analytics/cohorts`, { params, withCredentials: true });
        setCohortData(data);
      }
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, [cohortStatusFilter, cohortTypeFilter, excludedLearners]);

  useEffect(() => { setLoading(true); fetchCohorts(); }, [fetchCohorts]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/analytics/item-filter-options`, { withCredentials: true });
        setPsyFilterOptions(data);
      } catch { /* silent */ }
    })();
  }, []);

  // Fetch roster for learner panel cohort
  useEffect(() => {
    if (!learnerPanelCohort || cohortRosters[learnerPanelCohort]) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/analytics/cohort-roster/${learnerPanelCohort}`, { withCredentials: true });
        setCohortRosters((prev) => ({ ...prev, [learnerPanelCohort]: data }));
      } catch { /* silent */ }
    })();
  }, [learnerPanelCohort]);


  /* ─── Tab 1 derived data ─── */
  const allTypes = useMemo(() => {
    const map = new Map();
    cohortData.forEach((c) => c.assessments?.forEach((a) => {
      if (a.has_data && !map.has(a.assessment_type_id)) map.set(a.assessment_type_id, a.name);
    }));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [cohortData]);

  // Phase 6 cohort-comparison: dynamic Specific Items panel population.
  // Items are pulled from the cohorts currently selected in the Cohorts panel
  // (or all cohorts if none selected), grouped by kind, and filtered by the
  // selected scoring object types (or all kinds if none selected).
  const cohortsForItems = useMemo(() => {
    if (selectedCohorts.length === 0) return cohortData;
    return cohortData.filter((c) => selectedCohorts.includes(c.cohort_id));
  }, [cohortData, selectedCohorts]);

  const availableSpecificItems = useMemo(() => {
    const tracks = new Map();
    const lexps = new Map();
    const histAss = new Map();
    cohortsForItems.forEach((c) => {
      (c.tracks || []).forEach((t) => { if (!tracks.has(t.track_id)) tracks.set(t.track_id, { id: t.track_id, name: t.name }); });
      (c.lexps || []).forEach((l) => { if (!lexps.has(l.lexp_id)) lexps.set(l.lexp_id, { id: l.lexp_id, name: l.name }); });
      (c.assessments || []).forEach((a) => { if (a.has_data && !histAss.has(a.assessment_type_id)) histAss.set(a.assessment_type_id, { id: a.assessment_type_id, name: a.name }); });
    });
    const includeKind = (k) => selectedScoringObjectTypes.length === 0 || selectedScoringObjectTypes.includes(k);
    return {
      track: includeKind("track") ? Array.from(tracks.values()) : [],
      lexp: includeKind("lexp") ? Array.from(lexps.values()) : [],
      historical_assessment: includeKind("historical_assessment") ? Array.from(histAss.values()) : [],
    };
  }, [cohortsForItems, selectedScoringObjectTypes]);

  const filtered = useMemo(() => {
    let d = cohortData;
    if (selectedCohorts.length) d = d.filter((c) => selectedCohorts.includes(c.cohort_id));
    return d;
  }, [cohortData, selectedCohorts]);

  // Generalised column source. Each item has {kind, id, name}; the table /
  // chart code looks up the right per-cohort row via `getItemDataForCohort`.
  // Default behaviour (no selections): show every assessment-type that has
  // data — preserves the pre-Step-? rendering exactly.
  const displayItems = useMemo(() => {
    if (selectedSpecificItems.length) {
      const arr = [];
      selectedSpecificItems.forEach(({ kind, id }) => {
        const pool = availableSpecificItems[kind] || [];
        const hit = pool.find((x) => x.id === id);
        if (hit) arr.push({ kind, id, name: hit.name });
      });
      return arr;
    }
    const arr = [];
    const showKind = (k) => selectedScoringObjectTypes.length === 0 || selectedScoringObjectTypes.includes(k);
    if (showKind("historical_assessment")) {
      allTypes.forEach((t) => arr.push({ kind: "historical_assessment", id: t.id, name: t.name }));
    }
    if (showKind("track") && selectedScoringObjectTypes.includes("track")) {
      availableSpecificItems.track.forEach((t) => arr.push({ kind: "track", id: t.id, name: t.name }));
    }
    if (showKind("lexp") && selectedScoringObjectTypes.includes("lexp")) {
      availableSpecificItems.lexp.forEach((l) => arr.push({ kind: "lexp", id: l.id, name: l.name }));
    }
    return arr;
  }, [selectedSpecificItems, selectedScoringObjectTypes, availableSpecificItems, allTypes]);

  // Backward-compat alias for legacy table/chart code that iterated `displayTypes`
  // expecting `{id, name}` items. New per-cohort lookup below is kind-aware.
  const displayTypes = displayItems;

  // Per-cohort row lookup for a given displayItem.
  const getItemDataForCohort = (cohort, item) => {
    if (!item || !cohort) return undefined;
    if (item.kind === "track") return cohort.tracks?.find((x) => x.track_id === item.id);
    if (item.kind === "lexp") return cohort.lexps?.find((x) => x.lexp_id === item.id);
    return cohort.assessments?.find((x) => x.assessment_type_id === item.id);
  };

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      let aV, bV;
      if (sortField === "cohort_name") { aV = a.cohort_name || ""; bV = b.cohort_name || ""; }
      else {
        const item = displayItems.find((d) => d.id === sortField);
        aV = item ? (getItemDataForCohort(a, item)?.[compMetric] ?? -1) : -1;
        bV = item ? (getItemDataForCohort(b, item)?.[compMetric] ?? -1) : -1;
      }
      return sortDir === "asc" ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
    });
  }, [filtered, sortField, sortDir, compMetric, displayItems]);

  const toggleSort = (f) => { if (sortField === f) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortField(f); setSortDir("asc"); } };

  const fetchNc = useCallback(async (cid, preId, postId) => {
    if (!preId || !postId) return;
    try {
      const { data } = await axios.get(`${API}/analytics/cohorts/${cid}/nc?pre_type_id=${preId}&post_type_id=${postId}`, { withCredentials: true });
      setNcResults((p) => ({ ...p, [cid]: data }));
    } catch { /* silent */ }
  }, []);

  const handleNcPairingChange = (cid, field, value) => {
    const cur = ncPairings[cid] || {};
    const upd = { ...cur, [field]: value };
    setNcPairings((p) => ({ ...p, [cid]: upd }));
    if (upd.pre && upd.post) fetchNc(cid, upd.pre, upd.post);
  };

  const barChartData = useMemo(() => sorted.filter((c) => c.has_data).map((c) => {
    const row = { name: c.cohort_name };
    displayItems.forEach((it) => { row[it.name] = getItemDataForCohort(c, it)?.[compMetric] ?? 0; });
    return row;
  }), [sorted, displayItems, compMetric]);

  // Y-axis domain — 0-100 by default; when yAxisFit is on, zoom to data with
  // a small padding so single-cluster groups are readable (SD < 5 etc.).
  const yDomain = useMemo(() => {
    if (!yAxisFit) return [0, 100];
    const vals = [];
    barChartData.forEach((r) => {
      Object.entries(r).forEach(([k, v]) => { if (k !== "name" && typeof v === "number") vals.push(v); });
    });
    if (vals.length === 0) return [0, 100];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = Math.max(max - min, 1);
    const pad = Math.max(Math.round(range * 0.15), 2);
    return [Math.max(0, Math.floor(min - pad)), Math.min(100, Math.ceil(max + pad))];
  }, [yAxisFit, barChartData]);

  const truncateLabel = (v) => (typeof v === "string" && v.length > 28 ? v.slice(0, 28) + "…" : v);

  const clearComparisonFilters = () => { setSelectedCohorts([]); setSelectedTypes([]); setSelectedScoringObjectTypes([]); setSelectedSpecificItems([]); setExcludedLearners({}); setLearnerPanelCohort(""); setCohortRosters({}); setLearnerPreType(""); setLearnerPostType(""); setCohortStatusFilter("active"); setLearnerStatusFilter("active"); setCohortTypeFilter("all"); };

  const exportComparison = () => {
    const rows = [];
    sorted.forEach((c) => {
      const row = { Cohort: c.cohort_name };
      displayItems.forEach((it) => { const a = getItemDataForCohort(c, it); row[`${it.name} Mean`] = a?.mean ?? ""; row[`${it.name} SD`] = a?.sd ?? ""; });
      rows.push(row);
    });
    exportCsv(rows, "cohort_comparison.csv");
  };

  /* ─── Learner view (within Comparative Analytics) ─── */
  const fetchLearnerData = useCallback(async (cohortIds) => {
    if (!cohortIds?.length) { setLearnerData({ assessment_types: [], learners: [] }); return; }
    setLearnerLoading(true);
    try {
      const payload = { cohort_ids: cohortIds };
      if (learnerStatusFilter !== "all") payload.learner_status = learnerStatusFilter;
      const { data } = await axios.post(`${API}/analytics/learners`, payload, { withCredentials: true });
      setLearnerData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load learners"); }
    finally { setLearnerLoading(false); }
  }, [learnerStatusFilter]);

  useEffect(() => {
    if (compView === "learner") fetchLearnerData(selectedCohorts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compView, selectedCohorts, learnerStatusFilter]);

  const learnerNcMap = useMemo(() => {
    if (!learnerPreType || !learnerPostType) return {};
    const map = {};
    (learnerData.learners || []).forEach((l) => {
      const pre = l.scores?.[learnerPreType];
      const post = l.scores?.[learnerPostType];
      if (pre != null && post != null) {
        if (post === pre) map[l.id] = { nc: 0, label: "No Change" };
        else if (post > pre) {
          const nc = pre >= 100 ? 0 : (post - pre) / (100 - pre);
          map[l.id] = { nc, label: "Gain" };
        } else {
          const nc = pre <= 0 ? 0 : (post - pre) / pre;
          map[l.id] = { nc, label: "Loss" };
        }
      }
    });
    return map;
  }, [learnerData.learners, learnerPreType, learnerPostType]);

  const learnerDisplayTypes = useMemo(() => {
    const types = learnerData.assessment_types || [];
    if (selectedTypes.length === 0) return types;
    return types.filter((t) => selectedTypes.includes(t.id));
  }, [learnerData.assessment_types, selectedTypes]);

  const learnerSorted = useMemo(() => {
    const list = learnerData.learners || [];
    if (!learnerSortField) return list;
    return [...list].sort((a, b) => {
      let aV, bV;
      if (learnerSortField === "name") { aV = a.name; bV = b.name; }
      else if (learnerSortField === "nc") { aV = learnerNcMap[a.id]?.nc ?? -999; bV = learnerNcMap[b.id]?.nc ?? -999; }
      else { aV = a.scores?.[learnerSortField] ?? -1; bV = b.scores?.[learnerSortField] ?? -1; }
      return learnerSortDir === "asc" ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
    });
  }, [learnerData.learners, learnerSortField, learnerSortDir, learnerNcMap]);

  const toggleLearnerSort = (f) => { if (learnerSortField === f) setLearnerSortDir((d) => d === "asc" ? "desc" : "asc"); else { setLearnerSortField(f); setLearnerSortDir("asc"); } };

  const learnerFiltered = useMemo(() => {
    if (!learnerSearch.trim()) return learnerSorted;
    const q = learnerSearch.toLowerCase();
    return learnerSorted.filter((l) => l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q));
  }, [learnerSorted, learnerSearch]);

  const learnerBarData = useMemo(() => learnerSorted.map((l) => {
    const row = { name: l.name };
    learnerDisplayTypes.forEach((t) => { row[t.name] = l.scores?.[t.id] ?? 0; });
    return row;
  }), [learnerSorted, learnerDisplayTypes]);

  const exportLearnerCsv = () => {
    const rows = learnerSorted.map((l) => {
      const row = { Name: l.name, Cohort: l.cohort_name };
      learnerDisplayTypes.forEach((t) => { row[t.name] = l.scores?.[t.id] ?? ""; });
      const ncEntry = learnerNcMap[l.id];
      row["NC"] = ncEntry ? `${(ncEntry.nc * 100).toFixed(1)}% (${ncEntry.label})` : "";
      return row;
    });
    exportCsv(rows, "learner_analytics.csv");
  };

  /* ─── Tab 3: Psychometrics ─── */
  const runPsychometrics = async () => {
    setPsyLoading(true);
    try {
      const { data } = await axios.post(`${API}/analytics/psychometrics`, {
        cohort_ids: psyCohortIds, type_ids: psyTypeIds, topic_tags: psyTopicTags,
        item_type: psyItemType && psyItemType !== "all" ? psyItemType : undefined,
      }, { withCredentials: true });
      setPsyResults(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Query failed"); }
    finally { setPsyLoading(false); }
  };

  const clearPsyFilters = () => { setPsyCohortIds([]); setPsyTypeIds([]); setPsyTopicTags([]); setPsyItemType(""); setPsyResults([]); setExpandedItem(null); };

  const exportPsy = () => exportCsv(psyResults.map((r) => ({
    "Item ID": r.item_id, "Item Title": r.item_title, Type: r.item_type, Topic: r.topic_tag,
    N: r.n, "P-Value": r.p_value, "Point Biserial": r.point_biserial ?? "",
    "Rolling P-Value": r.rolling_p_value ?? "", "Rolling PB": r.rolling_point_biserial ?? "",
    Flags: (r.flags || []).join("; "),
  })), "psychometrics_export.csv");

  const psySorted = useMemo(() => {
    if (!psySortField) return psyResults;
    return [...psyResults].sort((a, b) => {
      let aV = a[psySortField] ?? -999;
      let bV = b[psySortField] ?? -999;
      if (psySortField === "item_title" || psySortField === "topic_tag" || psySortField === "item_type") { aV = String(aV); bV = String(bV); }
      return psySortDir === "asc" ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
    });
  }, [psyResults, psySortField, psySortDir]);

  const togglePsySort = (f) => { if (psySortField === f) setPsySortDir((d) => d === "asc" ? "desc" : "asc"); else { setPsySortField(f); setPsySortDir("asc"); } };

  const fetchTrend = async (itemId) => {
    if (expandedItem === itemId) { setExpandedItem(null); return; }
    setExpandedItem(itemId);
    setTrendLoading(true);
    try {
      const { data } = await axios.get(`${API}/analytics/psychometrics/item/${itemId}/trend`, { withCredentials: true });
      setTrendData(data);
    } catch { toast.error("Failed to load trend"); }
    finally { setTrendLoading(false); }
  };

  /* ─── Survey Data helpers ─── */
  const fetchSurveyData = useCallback(async () => {
    setSurveyLoading(true);
    try {
      const payload = {
        view: surveyView,
        cohort_ids: surveySelectedCohorts,
        cohort_status: surveyCohortStatus,
        learner_status: surveyLearnerStatus,
        search: surveySearch,
        assessment_type_ids: surveySelectedAssessments,
      };
      const { data } = await axios.post(`${API}/analytics/survey`, payload, { withCredentials: true });
      setSurveyData(data);
    } catch { toast.error("Failed to load survey data"); }
    finally { setSurveyLoading(false); }
  }, [surveyView, surveySelectedCohorts, surveyCohortStatus, surveyLearnerStatus, surveySearch, surveySelectedAssessments]);

  useEffect(() => {
    if (layer === "survey") fetchSurveyData();
  }, [layer, fetchSurveyData]);

  /* ─── Telemetry fetch ─── */
  const fetchTelemetry = useCallback(async () => {
    setTelLoading(true);
    try {
      const payload = {
        cohort_ids: telSelectedCohorts,
        cohort_status: telSelectedCohorts.length > 0 ? null : telCohortStatus,
      };
      const { data } = await axios.post(`${API}/analytics/telemetry`, payload, { withCredentials: true });
      setTelData(data);
    } catch { toast.error("Failed to load telemetry"); }
    finally { setTelLoading(false); }
  }, [telSelectedCohorts, telCohortStatus]);

  useEffect(() => {
    if (layer === "telemetry") fetchTelemetry();
  }, [layer, fetchTelemetry]);

  /* ─── MIND CFI Matrix fetch ───
     Surfaces only `cfi_mode=true` LEXPs deployed in tracks. Each completing
     learner is one quadrant point per (cohort, track). Backend handles
     completion gating, quadrant classification, and centroids. */
  const [cfiData, setCfiData] = useState({ cohorts: [], tracks: [], learners: [], rollups: {} });
  const [cfiLoading, setCfiLoading] = useState(false);
  const [cfiSelectedCohorts, setCfiSelectedCohorts] = useState([]);
  const [cfiSelectedTracks, setCfiSelectedTracks] = useState([]);

  const fetchCfi = useCallback(async () => {
    setCfiLoading(true);
    try {
      const params = new URLSearchParams();
      if (cfiSelectedCohorts.length) params.append("cohort_ids", cfiSelectedCohorts.join(","));
      if (cfiSelectedTracks.length) params.append("track_ids", cfiSelectedTracks.join(","));
      const qs = params.toString();
      const { data } = await axios.get(`${API}/analytics/cfi${qs ? "?" + qs : ""}`, { withCredentials: true });
      setCfiData(data);
    } catch { toast.error("Failed to load CFI data"); }
    finally { setCfiLoading(false); }
  }, [cfiSelectedCohorts, cfiSelectedTracks]);

  useEffect(() => {
    if (layer === "cfi") fetchCfi();
  }, [layer, fetchCfi]);

  const toggleSurveySort = (f) => { if (surveySortField === f) setSurveySortDir((d) => d === "asc" ? "desc" : "asc"); else { setSurveySortField(f); setSurveySortDir("asc"); } };

  const surveySorted = useMemo(() => {
    const list = surveyView === "cohort" ? (surveyData.cohorts || []) : (surveyData.learners || []);
    if (!surveySortField) return list;
    return [...list].sort((a, b) => {
      let aV, bV;
      if (surveySortField === "name" || surveySortField === "cohort_name") {
        aV = surveyView === "cohort" ? (a.cohort_name || "") : (a.name || "");
        bV = surveyView === "cohort" ? (b.cohort_name || "") : (b.name || "");
      } else {
        const aScore = a.item_scores?.[surveySortField];
        const bScore = b.item_scores?.[surveySortField];
        if (surveyView === "cohort") {
          aV = aScore?.mean ?? aScore?.n ?? -1;
          bV = bScore?.mean ?? bScore?.n ?? -1;
        } else {
          aV = aScore?.value ?? -1;
          bV = bScore?.value ?? -1;
        }
      }
      return surveySortDir === "asc" ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
    });
  }, [surveyData, surveyView, surveySortField, surveySortDir]);

  const surveyFiltered = useMemo(() => {
    if (!surveySearch.trim() || surveyView !== "learner") return surveySorted;
    const q = surveySearch.toLowerCase();
    return surveySorted.filter((l) => l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q));
  }, [surveySorted, surveySearch, surveyView]);

  const exportSurveyCsv = () => {
    const items = surveyData.survey_items || [];
    const rows = surveyFiltered.map((row) => {
      const r = { Name: surveyView === "cohort" ? row.cohort_name : row.name };
      items.forEach((si) => {
        const sc = row.item_scores?.[si.id];
        if (surveyView === "cohort") {
          r[si.title] = sc?.mean ?? sc?.n ?? "";
        } else {
          if (sc?.value != null) {
            r[si.title] = sc.value;
          } else if (sc?.selected) {
            const optMap = Object.fromEntries((si.answer_options || []).map(o => [o.option_id, o.text]));
            r[si.title] = sc.selected.map(oid => optMap[oid] || oid).join(", ");
          } else {
            r[si.title] = "";
          }
        }
      });
      return r;
    });
    exportCsv(rows, "survey_data.csv");
  };

  /* ─── Loading ─── */
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} /></div>;

  const cohortsWithData = cohortData.filter((c) => c.has_data);

  /* ═══════════════════════════════════
     RENDER
     ═══════════════════════════════════ */
  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="analytics-title">Analytics</h2>
          <p className="font-body text-sm mt-1" style={{ color: "var(--admin-text)", opacity: 0.6 }}>{cohortData.length} cohort{cohortData.length !== 1 ? "s" : ""} tracked</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }} data-testid="analytics-tabs">
        {[{ k: "comparison", l: "Comparative Analytics" }, { k: "psychometrics", l: "Psychometrics" }, { k: "telemetry", l: "Telemetry" }, { k: "survey", l: "Survey Data" }, { k: "cfi", l: "CFI Matrix" }].map(({ k, l }) => (
          <button key={k} onClick={() => setLayer(k)} data-testid={`layer-${k}-tab`}
            className="py-2 px-4 rounded-md text-sm font-body transition-colors"
            style={{
              backgroundColor: layer === k ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
              color: "var(--admin-text)",
              opacity: layer === k ? 1 : 0.5,
              fontWeight: layer === k ? 600 : 400,
            }}
          >{l}</button>
        ))}
      </div>

      {/* ════════════════════════════════════════════
          TAB 1: COHORT COMPARISON
          ════════════════════════════════════════════ */}
      {layer === "comparison" && (
        <div className="space-y-4">
          {/* Cohort / Learner toggle */}
          <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }} data-testid="comp-view-toggle">
            {[{ v: "cohort", l: "Cohort" }, { v: "learner", l: "Learner" }].map(({ v, l }) => (
              <button key={v} onClick={() => setCompView(v)} data-testid={`comp-view-${v}-btn`}
                className="py-1.5 px-4 rounded-md text-xs font-body transition-colors"
                style={{
                  backgroundColor: compView === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                  color: "var(--admin-text)",
                  opacity: compView === v ? 1 : 0.5,
                  fontWeight: compView === v ? 600 : 400,
                }}
              >{l}</button>
            ))}
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-action-bar">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>
              <Filter className="h-3.5 w-3.5 mr-1.5" />Filters {showFilters ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
            {compView === "cohort" && (
              <select
                value={compMetric}
                onChange={(e) => setCompMetric(e.target.value)}
                data-testid="comp-metric-select"
                className="h-8 rounded-md text-xs font-body px-2.5 cursor-pointer focus:outline-none"
                style={{ backgroundColor: "transparent", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-muted)" }}
              >
                <option value="mean">Mean</option>
                <option value="median">Median</option>
                <option value="sd">Standard Deviation</option>
              </select>
            )}
            {compView === "learner" && learnerLoading && <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />}
            <Button variant="outline" size="sm" onClick={compView === "cohort" ? exportComparison : exportLearnerCsv} data-testid="export-l1-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>
              <Download className="h-3.5 w-3.5 mr-1.5" />CSV
            </Button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 space-y-3" data-testid="filters-panel">
              <div className="flex items-center gap-4 flex-wrap mb-2">
                <div className="flex items-center gap-2">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Cohorts</Label>
                  <FilterToggle options={[{ v: "active", l: "Active" }, { v: "archived", l: "Archived" }, { v: "all", l: "Both" }]} value={cohortStatusFilter} onChange={setCohortStatusFilter} testIdPrefix="cohort-status" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Type</Label>
                  <FilterToggle options={[{ v: "all", l: "All" }, { v: "standard", l: "Standard" }, { v: "micro", l: "Micro" }]} value={cohortTypeFilter} onChange={setCohortTypeFilter} testIdPrefix="cohort-type" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Learners</Label>
                  <FilterToggle options={[{ v: "active", l: "Active" }, { v: "inactive", l: "Inactive" }, { v: "all", l: "Both" }]} value={learnerStatusFilter} onChange={setLearnerStatusFilter} testIdPrefix="learner-status" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Cohorts</Label>
                  <CheckboxList items={cohortData.map((c) => ({ id: c.cohort_id, name: `${c.cohort_name}${!c.has_data ? " (no data)" : ""}` }))} selected={selectedCohorts} toggle={(id) => setSelectedCohorts((p) => toggleIn(p, id))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Scoring Object Type</Label>
                  <CheckboxList
                    items={[
                      { id: "track", name: "Track" },
                      { id: "lexp", name: "LEXP" },
                      { id: "historical_assessment", name: "Historical Assessment" },
                    ]}
                    selected={selectedScoringObjectTypes}
                    toggle={(id) => setSelectedScoringObjectTypes((p) => toggleIn(p, id))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Learners</Label>
                  <div className="space-y-2">
                    <select value={learnerPanelCohort} onChange={(e) => setLearnerPanelCohort(e.target.value)}
                      className="w-full h-8 rounded-md text-xs font-body px-2 bg-[#0A0C10] border border-white/10 text-white"
                      data-testid="learner-cohort-select">
                      <option value="">Select a cohort</option>
                      {cohortData.filter((c) => selectedCohorts.length === 0 || selectedCohorts.includes(c.cohort_id)).map((c) => (
                        <option key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</option>
                      ))}
                    </select>
                    {learnerPanelCohort && (() => {
                      const roster = cohortRosters[learnerPanelCohort] || [];
                      const excluded = excludedLearners[learnerPanelCohort] || [];
                      const included = roster.length - excluded.length;
                      const toggleLearner = (lid) => {
                        setExcludedLearners((prev) => {
                          const curr = prev[learnerPanelCohort] || [];
                          const next = curr.includes(lid) ? curr.filter((x) => x !== lid) : [...curr, lid];
                          return { ...prev, [learnerPanelCohort]: next };
                        });
                      };
                      return (
                        <div>
                          <div className="max-h-40 overflow-y-auto space-y-0.5 pr-1">
                            {roster.length === 0 ? (
                              <p className="text-[10px] font-body" style={{ color: "var(--admin-container-muted)" }}>Loading...</p>
                            ) : roster.map((l) => {
                              const isExcluded = excluded.includes(l.id);
                              return (
                                <label key={l.id} className="flex items-center gap-2 py-0.5 cursor-pointer" data-testid={`learner-check-${l.id}`}>
                                  <input type="checkbox" checked={!isExcluded} onChange={() => toggleLearner(l.id)}
                                    className="rounded border-white/20 bg-transparent h-3.5 w-3.5 accent-[var(--primary-color)]" />
                                  <span className="text-xs font-body truncate" style={{ color: isExcluded ? "var(--admin-container-muted)" : "var(--admin-container-text)", opacity: isExcluded ? 0.5 : 1 }}>
                                    {l.first_name} {l.last_name}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          {excluded.length > 0 && (
                            <p className="text-[10px] font-body mt-1.5" style={{ color: "var(--primary-color)" }}>{included} of {roster.length} learners included</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              {/* Specific Items panel — populates dynamically from selected
                  cohorts + selected scoring object types. Shows ALL items
                  when nothing is selected upstream. */}
              <div className="space-y-1.5" data-testid="specific-items-panel">
                <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Specific Items</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { kind: "track", label: "Tracks" },
                    { kind: "lexp", label: "LEXPs" },
                    { kind: "historical_assessment", label: "Historical Assessments" },
                  ].map(({ kind, label }) => {
                    const pool = availableSpecificItems[kind] || [];
                    if (pool.length === 0 && selectedScoringObjectTypes.length > 0 && !selectedScoringObjectTypes.includes(kind)) return null;
                    return (
                      <div key={kind} className="space-y-1" data-testid={`specific-items-${kind}`}>
                        <Label className="font-body text-[10px] uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>{label}</Label>
                        <CheckboxList
                          items={pool}
                          selected={selectedSpecificItems.filter((s) => s.kind === kind).map((s) => s.id)}
                          toggle={(id) => setSelectedSpecificItems((prev) => {
                            const exists = prev.some((s) => s.kind === kind && s.id === id);
                            return exists
                              ? prev.filter((s) => !(s.kind === kind && s.id === id))
                              : [...prev, { kind, id }];
                          })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearComparisonFilters} data-testid="clear-comparison-filters-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>Clear Filters</Button>
              </div>
            </div>
          )}

          {/* View Toggle — hidden on mobile (cards shown instead), with chart toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hidden sm:flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }}>
              {[{ v: "table", icon: Table2, l: "Table" }, { v: "bar", icon: BarChart3, l: "Bar" }, { v: "line", icon: LineChart, l: "Line" }].map(({ v, icon: Icon, l }) => (
                <button key={v} onClick={() => setViewMode(v)} data-testid={`view-${v}-btn`}
                  className="flex items-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-body transition-colors"
                  style={{
                    backgroundColor: viewMode === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                    color: "var(--admin-text)",
                    opacity: viewMode === v ? 1 : 0.5,
                    fontWeight: viewMode === v ? 600 : 400,
                  }}
                ><Icon className="h-3.5 w-3.5" />{l}</button>
              ))}
            </div>
            {(viewMode === "bar" || viewMode === "line") && (
              <button
                onClick={() => setYAxisFit(!yAxisFit)}
                data-testid="y-axis-fit-toggle"
                title={yAxisFit ? "Showing zoomed Y-axis — click to reset to 0-100" : "Zoom Y-axis to data range"}
                className="hidden sm:flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-body transition-colors"
                style={{
                  backgroundColor: yAxisFit ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                  color: "var(--admin-text)",
                  opacity: yAxisFit ? 1 : 0.6,
                  border: `1px solid var(--admin-container-border)`,
                  fontWeight: yAxisFit ? 600 : 400,
                }}
              >
                <span style={{ fontSize: 12 }}>⇅</span>
                {yAxisFit ? "Fit to Data" : "0–100"}
              </button>
            )}
            <button
              onClick={() => setShowMobileChart(!showMobileChart)}
              data-testid="mobile-chart-toggle"
              className="sm:hidden flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-body transition-colors"
              style={{
                backgroundColor: showMobileChart ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                color: "var(--admin-text)",
                opacity: showMobileChart ? 1 : 0.5,
                border: `1px solid var(--admin-container-border)`,
              }}
            >
              <BarChart3 className="h-3.5 w-3.5" />{showMobileChart ? "Hide Chart" : "View Chart"}
            </button>
          </div>

          {compView === "cohort" && (cohortsWithData.length === 0 ? (
            <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="analytics-empty">
              <BarChart3 className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-color, #4A90D9)" }} />
              <h3 className="font-body text-lg font-bold text-white mb-2">No analytics data yet</h3>
              <p className="text-[#6E7787] font-body text-sm">Import assessment scores to see analytics</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View (default on mobile) */}
              <div className="sm:hidden space-y-3" data-testid="cohort-cards-mobile">
                {sorted.filter((c) => {
                  if (displayTypes.length > 0) return displayTypes.some((t) => c.assessments?.find((a) => a.assessment_type_id === t.id));
                  return true;
                }).map((c) => {
                  const typesWithData = c.assessments?.filter((a) => a.has_data) || [];
                  return (
                    <div key={c.cohort_id} className={`bg-[#11141A] border border-white/10 rounded-xl p-4 ${!c.has_data ? "opacity-40" : ""}`} data-testid={`cohort-card-${c.cohort_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-sm font-body font-medium text-white">{c.cohort_name}</p>
                          {c.instructor && <p className="text-[10px] text-[#6E7787] font-body mt-0.5">{c.instructor}</p>}
                        </div>
                        <Badge className="bg-white/5 text-[#A1A9B8] border-white/10 text-[10px]">{c.status}</Badge>
                      </div>
                      <div className="space-y-2">
                        {displayTypes.map((t) => {
                          const a = getItemDataForCohort(c, t);
                          const val = a?.has_data ? a[compMetric] : null;
                          return (
                            <div key={`${t.kind || 'ass'}-${t.id}`} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                              <span className="text-xs font-body text-[#A1A9B8]">{t.name}</span>
                              {val != null ? (
                                <div className="text-right">
                                  <span className="text-sm font-body font-medium text-white">{val}</span>
                                  <span className="text-[10px] text-[#6E7787] ml-1.5">n={a.score_count}</span>
                                </div>
                              ) : <span className="text-[#6E7787] text-xs">—</span>}
                            </div>
                          );
                        })}
                      </div>
                      {typesWithData.length >= 2 && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <span className="text-[10px] font-body text-[#6E7787] uppercase tracking-wider">NC</span>
                          <div className="space-y-1.5 mt-1">
                            <select value={ncPairings[c.cohort_id]?.pre || ""} onChange={(e) => handleNcPairingChange(c.cohort_id, "pre", e.target.value)} className="rounded text-[10px] px-1.5 py-1 w-full" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                              <option value="">Pre</option>
                              {typesWithData.map((a) => <option key={a.assessment_type_id} value={a.assessment_type_id}>{a.name}</option>)}
                            </select>
                            <select value={ncPairings[c.cohort_id]?.post || ""} onChange={(e) => handleNcPairingChange(c.cohort_id, "post", e.target.value)} className="rounded text-[10px] px-1.5 py-1 w-full" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                              <option value="">Post</option>
                              {typesWithData.map((a) => <option key={a.assessment_type_id} value={a.assessment_type_id}>{a.name}</option>)}
                            </select>
                          </div>
                          {ncResults[c.cohort_id] && <div className="mt-1.5"><NcDisplay nc={ncResults[c.cohort_id].nc} label={ncResults[c.cohort_id].label} /></div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile Chart (toggled) */}
              {showMobileChart && (
                <div className="sm:hidden bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid="mobile-chart">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={barChartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" tick={{ fill: "#6E7787", fontSize: 9 }} angle={-35} textAnchor="end" interval={0} height={60} />
                      <YAxis domain={[0, 100]} tick={{ fill: "#6E7787", fontSize: 10 }} width={30} />
                      <Tooltip content={<ChartTooltipContent />} />
                      {displayTypes.map((t, i) => <Bar key={`${t.kind || 'ass'}-${t.id}`} dataKey={t.name} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2 border-t border-white/5 mt-2">
                    {displayTypes.map((t, i) => (
                      <div key={`${t.kind || 'ass'}-${t.id}`} className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-[10px] text-[#A1A9B8] font-body">{t.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Desktop Table */}
              {viewMode === "table" && (
                <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="analytics-table">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => toggleSort("cohort_name")}>
                            <span className="flex items-center gap-1">Cohort <ArrowUpDown className="h-3 w-3" /></span>
                          </th>
                          {displayTypes.map((t) => (
                            <th key={`${t.kind || 'ass'}-${t.id}`} className="text-center px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => toggleSort(t.id)}>
                              <span className="flex items-center justify-center gap-1">{t.name} <ArrowUpDown className="h-3 w-3" /></span>
                            </th>
                          ))}
                          <th className="text-center px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">NC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.filter((c) => {
                          // When filters are active, hide cohorts that don't
                          // carry any of the displayed scoring objects.
                          if (displayTypes.length > 0) {
                            return displayTypes.some((t) => getItemDataForCohort(c, t));
                          }
                          return true;
                        }).map((c) => {
                          const typesWithData = c.assessments?.filter((a) => a.has_data) || [];
                          return (
                            <tr key={c.cohort_id} className={`border-b border-white/5 ${c.has_data ? "hover:bg-white/[0.02]" : "opacity-40"}`} data-testid={`analytics-row-${c.cohort_id}`}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-body font-medium text-white">{c.cohort_name}</p>
                                {c.instructor && <p className="text-[10px] text-[#6E7787] font-body">{c.instructor}</p>}
                              </td>
                              {displayTypes.map((t) => {
                                const a = getItemDataForCohort(c, t);
                                const val = a?.has_data ? a[compMetric] : null;
                                return (
                                  <td key={`${t.kind || 'ass'}-${t.id}`} className="text-center px-4 py-3">
                                    {val != null ? (<div><p className="text-sm font-body font-medium text-white">{val}</p><p className="text-[10px] text-[#6E7787] font-body">n={a.score_count}</p></div>) : <span className="text-[#6E7787] text-xs">—</span>}
                                  </td>
                                );
                              })}
                              <td className="text-center px-4 py-3">
                                {typesWithData.length >= 2 ? (
                                  <div className="space-y-1">
                                    <div className="flex gap-1 justify-center">
                                      <select value={ncPairings[c.cohort_id]?.pre || ""} onChange={(e) => handleNcPairingChange(c.cohort_id, "pre", e.target.value)} className="rounded text-[10px] px-1 py-0.5 w-20" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }} data-testid={`nc-pre-${c.cohort_id}`}>
                                        <option value="">Pre</option>
                                        {typesWithData.map((a) => <option key={a.assessment_type_id} value={a.assessment_type_id}>{a.name}</option>)}
                                      </select>
                                      <select value={ncPairings[c.cohort_id]?.post || ""} onChange={(e) => handleNcPairingChange(c.cohort_id, "post", e.target.value)} className="rounded text-[10px] px-1 py-0.5 w-20" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }} data-testid={`nc-post-${c.cohort_id}`}>
                                        <option value="">Post</option>
                                        {typesWithData.map((a) => <option key={a.assessment_type_id} value={a.assessment_type_id}>{a.name}</option>)}
                                      </select>
                                    </div>
                                    {ncResults[c.cohort_id] && <NcDisplay nc={ncResults[c.cohort_id].nc} label={ncResults[c.cohort_id].label} />}
                                  </div>
                                ) : <span className="text-[#6E7787] text-[10px]">Need 2+ types</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Desktop Bar */}
              {viewMode === "bar" && (
                <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl p-5 overflow-hidden" data-testid="analytics-bar-chart">
                  <ResponsiveContainer width="100%" height={460}>
                    <BarChart data={barChartData} margin={{ top: 10, right: 20, left: 40, bottom: 130 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" tick={{ fill: "#6E7787", fontSize: 11 }} tickFormatter={truncateLabel} angle={-30} textAnchor="end" interval={0} height={130} tickMargin={8} />
                      <YAxis domain={yDomain} tick={{ fill: "#6E7787", fontSize: 11 }} />
                      <Tooltip content={<ChartTooltipContent />} />
                      {displayTypes.map((t, i) => <Bar key={`${t.kind || 'ass'}-${t.id}`} dataKey={t.name} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />)}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-white/5 mt-2">
                    {displayTypes.map((t, i) => (
                      <div key={`${t.kind || 'ass'}-${t.id}`} className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-[#A1A9B8] font-body">{t.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Desktop Line */}
              {viewMode === "line" && (
                <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl p-5 overflow-hidden" data-testid="analytics-line-chart">
                  <ResponsiveContainer width="100%" height={460}>
                    <RLineChart data={barChartData} margin={{ top: 10, right: 20, left: 40, bottom: 130 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="name" tick={{ fill: "#6E7787", fontSize: 11 }} tickFormatter={truncateLabel} angle={-30} textAnchor="end" interval={0} height={130} tickMargin={8} />
                      <YAxis domain={yDomain} tick={{ fill: "#6E7787", fontSize: 11 }} />
                      <Tooltip content={<ChartTooltipContent />} />
                      {displayTypes.map((t, i) => <Line key={`${t.kind || 'ass'}-${t.id}`} type="monotone" dataKey={t.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />)}
                    </RLineChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-white/5 mt-2">
                    {displayTypes.map((t, i) => (
                      <div key={`${t.kind || 'ass'}-${t.id}`} className="flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-[#A1A9B8] font-body">{t.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ))}

          {/* ─── Learner View ─── */}
          {compView === "learner" && (
            learnerData.learners.length === 0 ? (
              <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="learner-empty">
                <BarChart3 className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-color, #4A90D9)" }} />
                <h3 className="font-body text-lg font-bold text-white mb-2">{selectedCohorts.length === 0 ? "Select cohorts to view learner data" : "No learner data found"}</h3>
                <p className="text-[#6E7787] font-body text-sm">{selectedCohorts.length === 0 ? "Use the Filters to select one or more cohorts" : "No assessment scores for selected cohorts"}</p>
              </div>
            ) : (
              <>
                {/* NC Global Selector */}
                {(learnerData.assessment_types || []).length >= 2 && (
                  <div className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid="learner-nc-selector">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-xs font-body font-medium uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>Normalized Change</span>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>Pre</Label>
                        <select value={learnerPreType} onChange={(e) => setLearnerPreType(e.target.value)} data-testid="learner-nc-pre-select" className="h-8 rounded-md text-xs font-body px-2.5 min-w-[140px] cursor-pointer focus:outline-none" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                          <option value="">Select Pre...</option>
                          {(learnerData.assessment_types || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>Post</Label>
                        <select value={learnerPostType} onChange={(e) => setLearnerPostType(e.target.value)} data-testid="learner-nc-post-select" className="h-8 rounded-md text-xs font-body px-2.5 min-w-[140px] cursor-pointer focus:outline-none" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                          <option value="">Select Post...</option>
                          {(learnerData.assessment_types || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      {learnerPreType && learnerPostType && (
                        <span className="text-[10px] font-body italic" style={{ color: "var(--admin-container-muted)" }}>NC = (Post - Pre) / (100 - Pre) for gains</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Mobile Learner Search + Card View */}
                <div className="sm:hidden space-y-3" data-testid="learner-cards-mobile">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--admin-container-muted)" }} />
                    <Input
                      value={learnerSearch}
                      onChange={(e) => setLearnerSearch(e.target.value)}
                      placeholder="Search learners..."
                      data-testid="learner-mobile-search"
                      className="pl-9 h-9 text-sm"
                      style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                    />
                  </div>
                  {learnerFiltered.map((l) => {
                    const ncEntry = learnerNcMap[l.id];
                    return (
                      <div key={l.id} className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid={`learner-card-${l.id}`}>
                        <p className="text-sm font-body font-medium text-white">{l.name}</p>
                        <p className="text-[10px] text-[#6E7787] font-body mb-3">{l.email}</p>
                        <div className="space-y-2">
                          {learnerDisplayTypes.map((t) => (
                            <div key={t.id} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                              <span className="text-xs font-body text-[#A1A9B8]">{t.name}</span>
                              <span className="text-sm font-body font-medium text-white">{l.scores?.[t.id] != null ? l.scores[t.id] : <span className="text-[#6E7787]">—</span>}</span>
                            </div>
                          ))}
                        </div>
                        {(learnerPreType && learnerPostType) && (
                          <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                            <span className="text-[10px] font-body text-[#6E7787] uppercase tracking-wider">NC</span>
                            {ncEntry ? <NcDisplay nc={ncEntry.nc} label={ncEntry.label} /> : <span className="text-[#6E7787] text-xs">—</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="text-xs text-[#6E7787] font-body">{learnerFiltered.length}{learnerSearch ? ` of ${learnerSorted.length}` : ""} learner{learnerFiltered.length !== 1 ? "s" : ""}</div>
                </div>

                {/* Mobile Learner Chart (toggled) */}
                {showMobileChart && learnerBarData.length > 0 && (
                  <div className="sm:hidden bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid="mobile-learner-chart">
                    <ResponsiveContainer width="100%" height={Math.max(250, learnerSorted.length * 35)}>
                      <BarChart data={learnerBarData} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: "#6E7787", fontSize: 9 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: "#A1A9B8", fontSize: 9 }} width={80} />
                        <Tooltip content={<ChartTooltipContent />} />
                        {learnerDisplayTypes.map((t, i) => <Bar key={t.id} dataKey={t.name} fill={COLORS[i % COLORS.length]} radius={[0, 4, 4, 0]} />)}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Desktop Learner Table */}
                {viewMode === "table" && (
                  <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="learner-table">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => toggleLearnerSort("name")}>
                              <span className="flex items-center gap-1">Name <ArrowUpDown className="h-3 w-3" /></span>
                            </th>
                            {learnerDisplayTypes.map((t) => (
                              <th key={t.id} className="text-center px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => toggleLearnerSort(t.id)}>
                                <span className="flex items-center justify-center gap-1">{t.name} <ArrowUpDown className="h-3 w-3" /></span>
                              </th>
                            ))}
                            <th className="text-center px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer hover:text-white" onClick={() => toggleLearnerSort("nc")}>
                              <span className="flex items-center justify-center gap-1">NC <ArrowUpDown className="h-3 w-3" /></span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {learnerSorted.map((l) => {
                            const ncEntry = learnerNcMap[l.id];
                            return (
                              <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`learner-row-${l.id}`}>
                                <td className="px-4 py-3">
                                  <p className="text-sm font-body font-medium text-white">{l.name}</p>
                                  <p className="text-[10px] text-[#6E7787] font-body">{l.email}</p>
                                </td>
                                {learnerDisplayTypes.map((t) => (
                                  <td key={t.id} className="text-center px-4 py-3 text-sm font-body text-white">
                                    {l.scores?.[t.id] != null ? l.scores[t.id] : <span className="text-[#6E7787]">—</span>}
                                  </td>
                                ))}
                                <td className="text-center px-4 py-3">
                                  {ncEntry ? <NcDisplay nc={ncEntry.nc} label={ncEntry.label} /> : <span className="text-[#6E7787] text-xs">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-2 border-t border-white/5 text-xs text-[#6E7787] font-body">{learnerSorted.length} learner{learnerSorted.length !== 1 ? "s" : ""}</div>
                  </div>
                )}

                {/* Desktop Learner Bar Chart */}
                {viewMode === "bar" && (
                  <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl p-5 overflow-hidden" data-testid="learner-bar-chart">
                    <ResponsiveContainer width="100%" height={Math.max(350, learnerSorted.length * 40)}>
                      <BarChart data={learnerBarData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis type="number" domain={yAxisFit ? ["dataMin", "dataMax"] : [0, 100]} tick={{ fill: "#6E7787", fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: "#A1A9B8", fontSize: 10 }} tickFormatter={truncateLabel} width={150} />
                        <Tooltip content={<ChartTooltipContent />} />
                        {learnerDisplayTypes.map((t, i) => <Bar key={t.id} dataKey={t.name} fill={COLORS[i % COLORS.length]} radius={[0, 4, 4, 0]} />)}
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-white/5 mt-2">
                      {learnerDisplayTypes.map((t, i) => (
                        <div key={t.id} className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-[#A1A9B8] font-body">{t.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Desktop Learner Line Chart */}
                {viewMode === "line" && (
                  <div className="hidden sm:block bg-[#11141A] border border-white/10 rounded-xl p-5 overflow-hidden" data-testid="learner-line-chart">
                    <ResponsiveContainer width="100%" height={460}>
                      <RLineChart data={learnerBarData} margin={{ top: 10, right: 20, left: 40, bottom: 130 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" tick={{ fill: "#6E7787", fontSize: 11 }} tickFormatter={truncateLabel} angle={-30} textAnchor="end" interval={0} height={130} tickMargin={8} />
                        <YAxis domain={yAxisFit ? ["dataMin", "dataMax"] : [0, 100]} tick={{ fill: "#6E7787", fontSize: 11 }} />
                        <Tooltip content={<ChartTooltipContent />} />
                        {learnerDisplayTypes.map((t, i) => <Line key={t.id} type="monotone" dataKey={t.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />)}
                      </RLineChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-white/5 mt-2">
                      {learnerDisplayTypes.map((t, i) => (
                        <div key={t.id} className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-[#A1A9B8] font-body">{t.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}


      {/* ════════════════════════════════════════════
          TAB 3: PSYCHOMETRICS
          ════════════════════════════════════════════ */}
      {layer === "psychometrics" && (
        <div className="space-y-4" data-testid="psychometrics-analytics">
          {/* Filters */}
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-5 space-y-4" data-testid="psy-filter-builder">
            <h3 className="font-body text-base font-bold" style={{ color: "var(--admin-container-text)" }}>Psychometrics Filter Builder</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Cohorts</Label>
                <CheckboxList items={cohortData.map((c) => ({ id: c.cohort_id, name: c.cohort_name }))} selected={psyCohortIds} toggle={(id) => setPsyCohortIds((p) => toggleIn(p, id))} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Assessment Types</Label>
                <CheckboxList items={allTypes} selected={psyTypeIds} toggle={(id) => setPsyTypeIds((p) => toggleIn(p, id))} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Topic Tags</Label>
                <CheckboxList items={(psyFilterOptions.meta_tags || []).map((t) => ({ id: t.id, name: t.name }))} selected={psyTopicTags} toggle={(t) => setPsyTopicTags((p) => toggleIn(p, t))} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Item Type</Label>
                <Select value={psyItemType} onValueChange={setPsyItemType}>
                  <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }} data-testid="psy-item-type-select"><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {psyFilterOptions.item_types.map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={runPsychometrics} disabled={psyLoading} data-testid="psy-run-btn" className="h-9 px-4 font-body rounded-lg hover:opacity-90 text-xs" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
                {psyLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Run Query
              </Button>
              <Button variant="outline" size="sm" onClick={clearPsyFilters} data-testid="psy-clear-btn" className="text-xs h-9" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>Clear Filters</Button>
              {psyResults.length > 0 && (
                <Button variant="outline" size="sm" onClick={exportPsy} data-testid="export-psy-btn" className="text-xs h-9" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />CSV
                </Button>
              )}
            </div>
          </div>

          {psyResults.length > 0 && (
            <>
              {/* View toggle */}
              <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }}>
                {[{ v: "table", icon: Table2, l: "Table" }, { v: "bar", icon: BarChart3, l: "Bar" }].map(({ v, icon: Icon, l }) => (
                  <button key={v} onClick={() => setPsyView(v)} data-testid={`psy-view-${v}-btn`}
                    className="flex items-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-body transition-colors"
                    style={{
                      backgroundColor: psyView === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                      color: "var(--admin-text)",
                      opacity: psyView === v ? 1 : 0.5,
                      fontWeight: psyView === v ? 600 : 400,
                    }}
                  ><Icon className="h-3.5 w-3.5" />{l}</button>
                ))}
              </div>

              {/* Table */}
              {psyView === "table" && (
                <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="psy-table">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          {[
                            { f: "item_id", l: "Item ID", align: "left" },
                            { f: "item_type", l: "Type", align: "center" },
                            { f: "topic_tag", l: "Topic", align: "left" },
                            { f: "n", l: "N", align: "center" },
                            { f: "p_value", l: "P-Value", align: "center" },
                            { f: "point_biserial", l: "Pt Biserial", align: "center" },
                            { f: "rolling_p_value", l: "Rolling P", align: "center" },
                            { f: "rolling_point_biserial", l: "Rolling PB", align: "center" },
                            { f: null, l: "Flags", align: "center" },
                          ].map(({ f, l, align }) => (
                            <th key={l} className={`text-${align} px-3 py-3 text-[10px] font-body font-medium text-[#6E7787] uppercase tracking-wider ${f ? "cursor-pointer hover:text-white" : ""}`} onClick={f ? () => togglePsySort(f) : undefined}>
                              <span className={`flex items-center ${align === "center" ? "justify-center" : ""} gap-1`}>{l}{f && <ArrowUpDown className="h-2.5 w-2.5" />}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {psySorted.map((r, i) => (
                          <>
                            <tr key={r.item_id} onClick={() => fetchTrend(r.item_id)} className={`border-b border-white/5 cursor-pointer transition-colors ${expandedItem === r.item_id ? "bg-[#4A90D9]/10" : "hover:bg-white/[0.02]"} ${r.flags?.length ? "bg-red-500/[0.03]" : ""}`} data-testid={`psy-row-${i}`}>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-body text-white max-w-[180px] truncate">{r.item_title}</span>
                                  {r.item_db_id && (
                                    <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/items/${r.item_db_id}`); }} className="text-[#4A90D9] hover:text-white" title="Edit in Item Builder" data-testid={`psy-edit-${i}`}>
                                      <ExternalLink className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="text-center px-3 py-3"><Badge variant="outline" className="text-[9px] border-white/10 text-[#A1A9B8]">{r.item_type?.toUpperCase() || "—"}</Badge></td>
                              <td className="px-3 py-3 text-xs font-body text-[#A1A9B8]">{r.topic_tag || "—"}</td>
                              <td className="text-center px-3 py-3 text-xs font-body text-[#6E7787]">{r.n}</td>
                              <td className="text-center px-3 py-3 text-sm font-body font-medium text-white">{r.p_value?.toFixed(2)}</td>
                              <td className="text-center px-3 py-3 text-sm font-body font-medium text-white">{r.point_biserial != null ? r.point_biserial.toFixed(2) : "—"}</td>
                              <td className="text-center px-3 py-3 text-xs font-body text-[#6E7787]">{r.rolling_p_value != null ? r.rolling_p_value.toFixed(2) : "—"}</td>
                              <td className="text-center px-3 py-3 text-xs font-body text-[#6E7787]">{r.rolling_point_biserial != null ? r.rolling_point_biserial.toFixed(2) : "—"}</td>
                              <td className="text-center px-3 py-3">
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {(r.flags || []).map((f) => <FlagBadge key={f} flag={f} />)}
                                  {(!r.flags || r.flags.length === 0) && <span className="text-[#6E7787] text-[10px]">—</span>}
                                </div>
                              </td>
                            </tr>
                            {/* Trend panel */}
                            {expandedItem === r.item_id && (
                              <tr key={`trend-${r.item_id}`}>
                                <td colSpan={9} className="p-0">
                                  <div className="bg-[#0A0C10] border-t border-white/5 p-4" data-testid={`psy-trend-${i}`}>
                                    {trendLoading ? (
                                      <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#4A90D9]" /></div>
                                    ) : trendData.length === 0 ? (
                                      <p className="text-center text-[#6E7787] text-xs font-body py-4">No trend data available for this item</p>
                                    ) : (
                                      <div className="space-y-3">
                                        <h4 className="text-xs font-body font-semibold text-[#A1A9B8] uppercase">Item Trend — {r.item_title}</h4>
                                        <ResponsiveContainer width="100%" height={220}>
                                          <RLineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                            <XAxis dataKey="cohort_name" tick={{ fill: "#6E7787", fontSize: 10 }} />
                                            <YAxis domain={[0, 1]} tick={{ fill: "#6E7787", fontSize: 10 }} />
                                            <Tooltip content={<ChartTooltipContent />} />
                                            <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 10, color: "#A1A9B8", paddingTop: 10 }} />
                                            <ReferenceLine y={0.9} stroke="#FFB347" strokeDasharray="4 4" label={{ value: "Too Easy", fill: "#FFB347", fontSize: 9 }} />
                                            <ReferenceLine y={0.2} stroke="#E5634D" strokeDasharray="4 4" label={{ value: "Too Hard", fill: "#E5634D", fontSize: 9 }} />
                                            <Line type="monotone" dataKey="p_value" stroke="#4A90D9" strokeWidth={2} dot={{ r: 4 }} name="P-Value" />
                                            <Line type="monotone" dataKey="point_biserial" stroke="#50C878" strokeWidth={2} dot={{ r: 4 }} name="Point Biserial" connectNulls />
                                            <Line type="monotone" dataKey="rolling_p_value" stroke="#4A90D9" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Rolling P" />
                                            <Line type="monotone" dataKey="rolling_point_biserial" stroke="#50C878" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Rolling PB" connectNulls />
                                          </RLineChart>
                                        </ResponsiveContainer>
                                        <div className="overflow-x-auto">
                                          <table className="w-full">
                                            <thead><tr className="border-b border-white/10">
                                              <th className="text-left px-3 py-1.5 text-[10px] font-body text-[#6E7787]">Cohort</th>
                                              <th className="text-center px-3 py-1.5 text-[10px] font-body text-[#6E7787]">N</th>
                                              <th className="text-center px-3 py-1.5 text-[10px] font-body text-[#6E7787]">P-Value</th>
                                              <th className="text-center px-3 py-1.5 text-[10px] font-body text-[#6E7787]">Pt Biserial</th>
                                            </tr></thead>
                                            <tbody>
                                              {trendData.map((td, ti) => (
                                                <tr key={ti} className="border-b border-white/5">
                                                  <td className="px-3 py-1.5 text-xs font-body text-white">{td.cohort_name}</td>
                                                  <td className="text-center px-3 py-1.5 text-xs font-body text-[#6E7787]">{td.n}</td>
                                                  <td className="text-center px-3 py-1.5 text-xs font-body text-white">{td.p_value?.toFixed(2)}</td>
                                                  <td className="text-center px-3 py-1.5 text-xs font-body text-white">{td.point_biserial != null ? td.point_biserial.toFixed(2) : "—"}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bar chart */}
              {psyView === "bar" && (
                <div className="bg-[#11141A] border border-white/10 rounded-xl p-5" data-testid="psy-bar-chart">
                  <ResponsiveContainer width="100%" height={Math.max(350, psySorted.length * 32)}>
                    <BarChart data={psySorted.map((r) => ({ name: r.item_title.length > 28 ? r.item_title.slice(0, 28) + "…" : r.item_title, "P-Value": r.p_value, "Pt Biserial": r.point_biserial ?? 0 }))} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis type="number" domain={[0, 1]} tick={{ fill: "#6E7787", fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "#A1A9B8", fontSize: 10 }} width={160} />
                      <Tooltip content={<ChartTooltipContent />} />
                      <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, color: "#A1A9B8", paddingTop: 10 }} />
                      <Bar dataKey="P-Value" fill="#4A90D9" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="Pt Biserial" fill="#50C878" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Summary stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Total Items", value: psyResults.length, color: "text-white" },
                  { label: "Avg P-Value", value: psyResults.length ? (psyResults.reduce((s, r) => s + r.p_value, 0) / psyResults.length).toFixed(2) : "—", color: "text-white" },
                  { label: "Flagged Items", value: psyResults.filter((r) => r.flags?.length).length, color: "text-red-400" },
                  { label: "Review Required", value: psyResults.filter((r) => r.flags?.includes("Review Required")).length, color: "text-rose-400" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-[#11141A] border border-white/10 rounded-xl p-4 text-center">
                    <p className="text-[#6E7787] font-body text-xs mb-1">{label}</p>
                    <p className={`font-heading text-2xl ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════
          TELEMETRY ANALYTICS
          ════════════════════════════════════════════ */}
      {layer === "telemetry" && (
        <div className="space-y-4" data-testid="telemetry-analytics">
          {/* View toggle + filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }} data-testid="tel-view-toggle">
              {[{ v: "cohort", l: "Cohort" }, { v: "learner", l: "Learner" }].map(({ v, l }) => (
                <button key={v} onClick={() => setTelView(v)} data-testid={`tel-view-${v}-btn`}
                  className="py-1.5 px-4 rounded-md text-xs font-body transition-colors"
                  style={{
                    backgroundColor: telView === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                    color: "var(--admin-text)",
                    opacity: telView === v ? 1 : 0.5,
                    fontWeight: telView === v ? 600 : 400,
                  }}
                >{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setTelShowFilters(!telShowFilters)} data-testid="tel-toggle-filters-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>
                <Filter className="h-3.5 w-3.5 mr-1.5" />Filters {telShowFilters ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </Button>
              {telView === "learner" && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                  <Input value={telSearch} onChange={(e) => setTelSearch(e.target.value)} placeholder="Search learners..." data-testid="tel-learner-search"
                    className="pl-8 h-8 text-xs w-48"
                    style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Filter panel */}
          {telShowFilters && (
            <div className="p-4 rounded-xl border space-y-3" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }} data-testid="tel-filters">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-body mb-1 block" style={{ color: "var(--admin-container-muted)" }}>Cohort Status</Label>
                  <Select value={telCohortStatus} onValueChange={setTelCohortStatus}>
                    <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-body mb-1 block" style={{ color: "var(--admin-container-muted)" }}>Cohort</Label>
                  <Select value={telSelectedCohorts.length === 1 ? telSelectedCohorts[0] : "all"} onValueChange={(v) => setTelSelectedCohorts(v === "all" ? [] : [v])}>
                    <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                      <SelectValue placeholder="All Cohorts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cohorts</SelectItem>
                      {telData.cohorts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-body mb-1 block" style={{ color: "var(--admin-container-muted)" }}>Track</Label>
                  <Select value={telSelectedTrack} onValueChange={setTelSelectedTrack}>
                    <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}>
                      <SelectValue placeholder="All Tracks" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tracks</SelectItem>
                      {telData.tracks.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {telLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
          ) : telData.telemetry.length === 0 ? (
            <div className="text-center py-12 text-sm font-body" style={{ color: "var(--admin-container-muted)" }}>No telemetry data available for the selected filters.</div>
          ) : (
            <>
              {/* ── COHORT VIEW ── */}
              {telView === "cohort" && (() => {
                // Filter telemetry by selected track
                const filtered = telSelectedTrack === "all" ? telData.telemetry : telData.telemetry.filter((t) => t.track_id === telSelectedTrack);

                // Group by cohort -> track -> lexp with averages
                const cohortAgg = {};
                for (const row of filtered) {
                  if (!cohortAgg[row.cohort_id]) cohortAgg[row.cohort_id] = {};
                  const key = `${row.track_id}::${row.lexp_id}`;
                  if (!cohortAgg[row.cohort_id][key]) cohortAgg[row.cohort_id][key] = { track_id: row.track_id, lexp_id: row.lexp_id, lexp_label: row.lexp_label, values: [] };
                  cohortAgg[row.cohort_id][key].values.push(row.seconds);
                }

                const cohortRows = telData.cohorts.map((c) => {
                  const agg = cohortAgg[c.id] || {};
                  const allVals = Object.values(agg).flatMap((a) => a.values);
                  const avg = allVals.length > 0 ? Math.round(allVals.reduce((a, b) => a + b, 0) / allVals.length) : null;
                  const cohortTelemetry = filtered.filter((r) => r.cohort_id === c.id);
                  const learnerCount = new Set(cohortTelemetry.map((r) => r.learner_id)).size;
                  // Total track time per learner: sum all LEXP times per learner, then average
                  const learnerTotals = {};
                  for (const r of cohortTelemetry) {
                    learnerTotals[r.learner_id] = (learnerTotals[r.learner_id] || 0) + r.seconds;
                  }
                  const totals = Object.values(learnerTotals);
                  const totalAvg = totals.length > 0 ? Math.round(totals.reduce((a, b) => a + b, 0) / totals.length) : null;
                  return { ...c, agg, avgSeconds: avg, learnerCount, totalAvgSeconds: totalAvg };
                }).filter((c) => Object.keys(c.agg).length > 0);

                const fmtTime = (s) => { if (!s) return "—"; const m = Math.floor(s / 60); const sec = s % 60; if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; } return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };

                return (
                  <div className="space-y-3" data-testid="tel-cohort-view">
                    {cohortRows.map((c) => {
                      const isExpanded = telExpandedCohorts[c.id];
                      const tracks = {};
                      for (const item of Object.values(c.agg)) {
                        if (!tracks[item.track_id]) tracks[item.track_id] = { track: telData.tracks.find((t) => t.id === item.track_id), lexps: [] };
                        const avg = Math.round(item.values.reduce((a, b) => a + b, 0) / item.values.length);
                        tracks[item.track_id].lexps.push({ ...item, avg });
                      }
                      return (
                        <div key={c.id} className="border rounded-xl overflow-hidden" style={{ borderColor: "var(--admin-container-border)" }}>
                          <button onClick={() => setTelExpandedCohorts((p) => ({ ...p, [c.id]: !p[c.id] }))}
                            className="w-full flex items-center gap-4 px-4 py-3 text-left transition-colors hover:opacity-90"
                            style={{ backgroundColor: "var(--admin-container-bg)" }}
                            data-testid={`tel-cohort-row-${c.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-body font-semibold" style={{ color: "var(--admin-container-text)" }}>{c.name}</span>
                                <Badge className="text-[10px]" style={{ backgroundColor: "color-mix(in srgb, var(--primary-color) 15%, transparent)", color: "var(--primary-color)", border: "none" }}>
                                  {c.cohort_type === "micro" ? "Micro" : "Standard"}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-0.5 text-[11px] font-body" style={{ color: "var(--admin-container-muted)" }}>
                                <span>{c.learnerCount} learner{c.learnerCount !== 1 ? "s" : ""}</span>
                                <span>Avg: {fmtTime(c.avgSeconds)} per activity</span>
                                <span>Total avg: {fmtTime(c.totalAvgSeconds)}</span>
                              </div>
                            </div>
                            <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`} style={{ color: "var(--admin-container-muted)" }} />
                          </button>
                          {isExpanded && (
                            <div className="border-t px-4 py-3 space-y-4" style={{ borderColor: "var(--admin-container-border)", backgroundColor: "var(--admin-bg)" }}>
                              {Object.values(tracks).map(({ track, lexps }) => (
                                <div key={track?.id || "unknown"}>
                                  <p className="text-xs font-body font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--admin-container-muted)" }}>{track?.title || "Unknown Track"}</p>
                                  <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "var(--admin-container-border)" }}>
                                    <table className="w-full">
                                      <thead>
                                        <tr style={{ backgroundColor: "var(--admin-container-bg)" }}>
                                          <th className="text-left px-3 py-2 text-[11px] font-body font-medium uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>LEXP</th>
                                          <th className="text-right px-3 py-2 text-[11px] font-body font-medium uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>Avg Time</th>
                                          <th className="text-right px-3 py-2 text-[11px] font-body font-medium uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>Completions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lexps.map((lx) => (
                                          <tr key={lx.lexp_id} className="border-t" style={{ borderColor: "var(--admin-container-border)" }}>
                                            <td className="px-3 py-2 text-sm font-body" style={{ color: "var(--admin-container-text)" }}>{lx.lexp_label}</td>
                                            <td className="px-3 py-2 text-sm font-body text-right font-semibold" style={{ color: "var(--primary-color)" }}>{fmtTime(lx.avg)}</td>
                                            <td className="px-3 py-2 text-sm font-body text-right" style={{ color: "var(--admin-container-muted)" }}>{lx.values.length}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ── LEARNER VIEW ── */}
              {telView === "learner" && (() => {
                const filtered = telSelectedTrack === "all" ? telData.telemetry : telData.telemetry.filter((t) => t.track_id === telSelectedTrack);
                const search = telSearch.toLowerCase().trim();

                // Build learner rows
                const learnerAgg = {};
                for (const row of filtered) {
                  if (!learnerAgg[row.learner_id]) learnerAgg[row.learner_id] = [];
                  learnerAgg[row.learner_id].push(row);
                }

                let learnerRows = telData.learners.map((l) => {
                  const rows = learnerAgg[l.id] || [];
                  const totalSeconds = rows.reduce((a, r) => a + r.seconds, 0);
                  const avgSeconds = rows.length > 0 ? Math.round(totalSeconds / rows.length) : null;
                  const cohortNames = [...new Set(rows.map((r) => {
                    const c = telData.cohorts.find((x) => x.id === r.cohort_id);
                    return c ? c.name : "";
                  }).filter(Boolean))];
                  return { ...l, rows, totalSeconds, avgSeconds, lexpCount: rows.length, cohortNames };
                }).filter((l) => l.rows.length > 0);

                // Search filter
                if (search) {
                  learnerRows = learnerRows.filter((l) =>
                    `${l.first_name} ${l.last_name}`.toLowerCase().includes(search) || l.email.toLowerCase().includes(search)
                  );
                }

                // Sort
                const sortFn = (a, b) => {
                  let va, vb;
                  if (telSortField === "name") { va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; }
                  else if (telSortField === "avg") { va = a.avgSeconds || 0; vb = b.avgSeconds || 0; }
                  else if (telSortField === "total") { va = a.totalSeconds || 0; vb = b.totalSeconds || 0; }
                  else if (telSortField === "count") { va = a.lexpCount; vb = b.lexpCount; }
                  else { va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; }
                  if (typeof va === "string") return telSortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
                  return telSortDir === "asc" ? va - vb : vb - va;
                };
                learnerRows.sort(sortFn);

                const fmtTime = (s) => { if (!s) return "—"; const m = Math.floor(s / 60); const sec = s % 60; if (m >= 60) { const h = Math.floor(m / 60); return `${h}h ${m % 60}m`; } return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };
                const toggleSort = (f) => { if (telSortField === f) setTelSortDir((d) => d === "asc" ? "desc" : "asc"); else { setTelSortField(f); setTelSortDir("asc"); } };
                const SortIcon = ({ field }) => telSortField === field ? (telSortDir === "asc" ? <ChevronUp className="h-3 w-3 inline ml-0.5" /> : <ChevronDown className="h-3 w-3 inline ml-0.5" />) : <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-30" />;

                return (
                  <div data-testid="tel-learner-view">
                    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--admin-container-border)" }}>
                      <table className="w-full">
                        <thead>
                          <tr style={{ backgroundColor: "var(--admin-container-bg)" }}>
                            <th className="text-left px-3 py-2.5 text-[11px] font-body font-medium uppercase tracking-wider cursor-pointer select-none" style={{ color: "var(--admin-container-muted)" }} onClick={() => toggleSort("name")}>
                              Learner <SortIcon field="name" />
                            </th>
                            <th className="text-left px-3 py-2.5 text-[11px] font-body font-medium uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>Cohort</th>
                            <th className="text-right px-3 py-2.5 text-[11px] font-body font-medium uppercase tracking-wider cursor-pointer select-none" style={{ color: "var(--admin-container-muted)" }} onClick={() => toggleSort("count")}>
                              LEXPs <SortIcon field="count" />
                            </th>
                            <th className="text-right px-3 py-2.5 text-[11px] font-body font-medium uppercase tracking-wider cursor-pointer select-none" style={{ color: "var(--admin-container-muted)" }} onClick={() => toggleSort("avg")}>
                              Avg Time <SortIcon field="avg" />
                            </th>
                            <th className="text-right px-3 py-2.5 text-[11px] font-body font-medium uppercase tracking-wider cursor-pointer select-none" style={{ color: "var(--admin-container-muted)" }} onClick={() => toggleSort("total")}>
                              Total Time <SortIcon field="total" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {learnerRows.map((l) => {
                            const isExpanded = telExpandedCohorts[`l_${l.id}`];
                            return (
                              <React.Fragment key={l.id}>
                                <tr className="border-t cursor-pointer hover:opacity-90" style={{ borderColor: "var(--admin-container-border)" }}
                                  onClick={() => setTelExpandedCohorts((p) => ({ ...p, [`l_${l.id}`]: !p[`l_${l.id}`] }))}
                                  data-testid={`tel-learner-row-${l.id}`}>
                                  <td className="px-3 py-2.5 text-sm font-body font-medium" style={{ color: "var(--admin-container-text)" }}>
                                    <div className="flex items-center gap-2">
                                      <ChevronDown className={`h-3.5 w-3.5 transition-transform flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`} style={{ color: "var(--admin-container-muted)" }} />
                                      {l.first_name} {l.last_name}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5 text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>{l.cohortNames.join(", ")}</td>
                                  <td className="px-3 py-2.5 text-sm font-body text-right" style={{ color: "var(--admin-container-muted)" }}>{l.lexpCount}</td>
                                  <td className="px-3 py-2.5 text-sm font-body text-right font-semibold" style={{ color: "var(--primary-color)" }}>{fmtTime(l.avgSeconds)}</td>
                                  <td className="px-3 py-2.5 text-sm font-body text-right" style={{ color: "var(--admin-container-text)" }}>{fmtTime(l.totalSeconds)}</td>
                                </tr>
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={5} className="px-0 py-0">
                                      <div className="px-6 py-3 space-y-1" style={{ backgroundColor: "var(--admin-bg)" }}>
                                        {l.rows.sort((a, b) => a.seconds - b.seconds).map((r) => (
                                          <div key={`${r.lexp_id}-${r.track_id}-${r.cohort_id}`} className="flex items-center gap-3 py-1.5 px-3 rounded-lg text-xs font-body border" style={{ borderColor: "var(--admin-container-border)", backgroundColor: "var(--admin-container-bg)" }}>
                                            <span className="flex-1" style={{ color: "var(--admin-container-text)" }}>{r.lexp_label}</span>
                                            <span style={{ color: "var(--admin-container-muted)" }}>{telData.tracks.find((t) => t.id === r.track_id)?.title || ""}</span>
                                            <span className="font-semibold" style={{ color: "var(--primary-color)" }}>{fmtTime(r.seconds)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════
          TAB 3: SURVEY DATA
          ════════════════════════════════════════════ */}
      {layer === "survey" && (
        <div className="space-y-4" data-testid="survey-analytics">
          {/* Cohort / Learner toggle + actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }} data-testid="survey-view-toggle">
              {[{ v: "cohort", l: "Cohort" }, { v: "learner", l: "Learner" }].map(({ v, l }) => (
                <button key={v} onClick={() => setSurveyView(v)} data-testid={`survey-view-${v}-btn`}
                  className="py-1.5 px-4 rounded-md text-xs font-body transition-colors"
                  style={{
                    backgroundColor: surveyView === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
                    color: "var(--admin-text)",
                    opacity: surveyView === v ? 1 : 0.5,
                    fontWeight: surveyView === v ? 600 : 400,
                  }}
                >{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSurveyShowFilters(!surveyShowFilters)} data-testid="survey-toggle-filters-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>
                <Filter className="h-3.5 w-3.5 mr-1.5" />Filters {surveyShowFilters ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </Button>
              {surveyView === "learner" && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                  <Input value={surveySearch} onChange={(e) => setSurveySearch(e.target.value)} placeholder="Search learners..." data-testid="survey-learner-search"
                    className="pl-8 h-8 text-xs w-48"
                    style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                  />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={exportSurveyCsv} data-testid="survey-export-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                <Download className="h-3.5 w-3.5 mr-1" />Export
              </Button>
            </div>
          </div>

          {/* Filters panel */}
          {surveyShowFilters && (
            <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 space-y-3" data-testid="survey-filters-panel">
              <div className="flex items-center gap-4 flex-wrap mb-2">
                <div className="flex items-center gap-2">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Cohorts</Label>
                  <FilterToggle options={[{ v: "active", l: "Active" }, { v: "archived", l: "Archived" }, { v: "all", l: "Both" }]} value={surveyCohortStatus} onChange={setSurveyCohortStatus} testIdPrefix="survey-cohort-status" />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Learners</Label>
                  <FilterToggle options={[{ v: "active", l: "Active" }, { v: "inactive", l: "Inactive" }, { v: "all", l: "Both" }]} value={surveyLearnerStatus} onChange={setSurveyLearnerStatus} testIdPrefix="survey-learner-status" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Select Cohorts</Label>
                  <CheckboxList items={cohortData.map((c) => ({ id: c.cohort_id, name: c.cohort_name }))} selected={surveySelectedCohorts} toggle={(id) => setSurveySelectedCohorts((p) => toggleIn(p, id))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-body text-xs" style={{ color: "var(--admin-container-muted)" }}>Select Assessments</Label>
                  <CheckboxList items={(surveyData.assessment_types || [])} selected={surveySelectedAssessments} toggle={(id) => setSurveySelectedAssessments((p) => toggleIn(p, id))} testIdPrefix="survey-at" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setSurveySelectedCohorts([]); setSurveyCohortStatus("active"); setSurveyLearnerStatus("active"); setSurveySelectedAssessments([]); }} data-testid="survey-clear-filters-btn" className="text-xs h-8" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)", backgroundColor: "transparent" }}>Clear Filters</Button>
              </div>
            </div>
          )}

          {surveyLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
          ) : (surveyData.survey_items || []).length === 0 ? (
            <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="survey-empty">
              <p className="text-[#6E7787] font-body text-sm">No survey items found. Create questions with the "Survey" category in the Question Bank to see data here.</p>
            </div>
          ) : (() => {
            const allItems = surveyData.survey_items || [];
            const likertItems = allItems.filter((si) => si.item_type === "likert");
            const mcItems = allItems.filter((si) => si.item_type === "mc");
            const sataItems = allItems.filter((si) => si.item_type === "sata");
            const textItems = allItems.filter((si) => si.item_type !== "likert" && si.item_type !== "mc" && si.item_type !== "sata");
            const dataRows = surveyFiltered;

            return (
              <div className="space-y-6">
                {/* ═══ COHORT VIEW ═══ */}
                {surveyView === "cohort" && (
                  <>
                    {/* Likert tiles */}
                    {likertItems.length > 0 && (
                      <div data-testid="survey-likert-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Likert Scale Items</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {likertItems.map((si) => {
                            const labels = si.likert_labels || {};
                            const scaleMin = si.likert_scale_min ?? 0;
                            const scaleMax = si.likert_scale_max ?? 5;
                            const allVals = [];
                            dataRows.forEach((row) => {
                              const sc = row.item_scores?.[si.id];
                              if (sc?.type === "likert" && sc.distribution) {
                                Object.entries(sc.distribution).forEach(([k, count]) => {
                                  for (let i = 0; i < count; i++) allVals.push(Number(k));
                                });
                              }
                            });
                            const grandMean = allVals.length > 0 ? (allVals.reduce((s, v) => s + v, 0) / allVals.length).toFixed(2) : "—";
                            const totalN = allVals.length;
                            return (
                              <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-4 space-y-2" data-testid={`survey-likert-tile-${si.id}`}>
                                <p className="text-xs font-body font-medium text-white leading-snug line-clamp-2">{si.title}</p>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-2xl font-heading text-white">{grandMean}</span>
                                  <span className="text-xs text-[#6E7787] font-body">avg ({totalN} responses)</span>
                                </div>
                                <div className="text-[10px] text-[#6E7787] font-body flex justify-between">
                                  <span>{scaleMin}: {labels[String(scaleMin)] || ""}</span>
                                  <span>{scaleMax}: {labels[String(scaleMax)] || ""}</span>
                                </div>
                                {dataRows.length > 0 && (
                                  <div className="space-y-1 pt-1">
                                    {dataRows.map((row) => {
                                      const sc = row.item_scores?.[si.id];
                                      if (!sc || sc.type !== "likert") return null;
                                      const pct = scaleMax > scaleMin ? ((sc.mean - scaleMin) / (scaleMax - scaleMin)) * 100 : 0;
                                      return (
                                        <div key={row.cohort_id} className="flex items-center gap-2">
                                          <span className="text-[10px] text-[#A1A9B8] font-body w-28 truncate flex-shrink-0">{row.cohort_name}</span>
                                          <div className="flex-1 h-3 bg-[#0A0C10] rounded-full overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${Math.max(2, pct)}%`, backgroundColor: COLORS[0] }} />
                                          </div>
                                          <span className="text-[10px] text-white font-body font-medium w-8 text-right">{sc.mean}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* MC: Pie charts */}
                    {mcItems.length > 0 && (
                      <div data-testid="survey-mc-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Multiple Choice Items</h3>
                        {mcItems.map((si) => {
                          const optMap = {};
                          (si.answer_options || []).forEach((o) => { optMap[o.option_id] = o.text; });
                          const cohortsWithData = dataRows.filter((c) => c.item_scores?.[si.id]?.option_counts && Object.keys(c.item_scores[si.id].option_counts).length > 0);
                          return (
                            <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-5 mb-4" data-testid={`survey-mc-chart-${si.id}`}>
                              <p className="text-xs font-body font-medium text-white mb-4">{si.title}</p>
                              {cohortsWithData.length === 0 ? (
                                <p className="text-xs text-[#6E7787]">No responses yet.</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                  {cohortsWithData.map((c) => {
                                    const sc = c.item_scores[si.id];
                                    const pieData = Object.entries(sc.option_counts).map(([oid, count]) => ({
                                      name: optMap[oid] || oid,
                                      value: count,
                                      pct: sc.total > 0 ? Math.round((count / sc.total) * 100) : 0,
                                    }));
                                    return (
                                      <div key={c.cohort_id} className="bg-[#0A0C10] border border-white/5 rounded-lg p-4">
                                        <p className="text-[10px] font-body font-medium text-[#A1A9B8] mb-2 truncate uppercase tracking-wider">{c.cohort_name} <span className="text-[#6E7787]">(n={sc.n})</span></p>
                                        <ResponsiveContainer width="100%" height={180}>
                                          <PieChart>
                                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value" label={({ pct }) => `${pct}%`}>
                                              {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip formatter={(value, name) => [`${value} (${pieData.find(d => d.name === name)?.pct || 0}%)`, name]} contentStyle={{ backgroundColor: "#11141A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, color: "#fff" }} />
                                          </PieChart>
                                        </ResponsiveContainer>
                                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                          {pieData.map((d, i) => (
                                            <span key={d.name} className="inline-flex items-center gap-1.5 text-[10px] text-[#A1A9B8]">
                                              <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* SATA: Histograms */}
                    {sataItems.length > 0 && (
                      <div data-testid="survey-sata-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Select All That Apply Items</h3>
                        {sataItems.map((si) => {
                          const optMap = {};
                          (si.answer_options || []).forEach((o) => { optMap[o.option_id] = o.text; });
                          const allOpts = (si.answer_options || []).map((o) => o.option_id);
                          const chartData = allOpts.map((oid) => {
                            const row = { name: optMap[oid] || oid };
                            dataRows.forEach((c) => {
                              const sc = c.item_scores?.[si.id];
                              const shortName = c.cohort_name?.length > 20 ? c.cohort_name.substring(0, 20) + "..." : c.cohort_name;
                              row[shortName] = sc?.option_counts?.[oid] || 0;
                            });
                            return row;
                          });
                          const cohortNames = dataRows.map((c) => c.cohort_name?.length > 20 ? c.cohort_name.substring(0, 20) + "..." : c.cohort_name);
                          const totalN = dataRows.reduce((s, c) => s + (c.item_scores?.[si.id]?.n || 0), 0);
                          const perCohortN = dataRows.map((c) => ({
                            name: c.cohort_name?.length > 20 ? c.cohort_name.substring(0, 20) + "..." : c.cohort_name,
                            n: c.item_scores?.[si.id]?.n || 0,
                          })).filter((x) => x.n > 0);
                          return (
                            <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-5 mb-4" data-testid={`survey-sata-chart-${si.id}`}>
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <p className="text-xs font-body font-medium text-white">{si.title}</p>
                                <span className="shrink-0 text-[10px] font-body text-[#A1A9B8] bg-white/5 border border-white/10 rounded-full px-2 py-0.5" data-testid={`survey-sata-total-n-${si.id}`}>
                                  n={totalN}
                                </span>
                              </div>
                              {perCohortN.length > 1 && (
                                <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`survey-sata-cohort-n-${si.id}`}>
                                  {perCohortN.map((c) => (
                                    <span key={c.name} className="text-[10px] font-body text-[#6E7787]">
                                      {c.name}: <span className="text-[#A1A9B8]">n={c.n}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {!chartData.some((d) => Object.values(d).some((v) => typeof v === "number" && v > 0)) ? (
                                <p className="text-xs text-[#6E7787]">No responses yet.</p>
                              ) : (
                                <ResponsiveContainer width="100%" height={Math.max(200, allOpts.length * 50)}>
                                  <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <XAxis type="number" tick={{ fill: "#6E7787", fontSize: 11 }} allowDecimals={false} />
                                    <YAxis dataKey="name" type="category" tick={{ fill: "#A1A9B8", fontSize: 10 }} width={160} />
                                    <Tooltip content={<ChartTooltipContent />} />
                                    <Legend wrapperStyle={{ fontSize: 10, color: "#A1A9B8" }} />
                                    {cohortNames.map((cn, i) => (
                                      <Bar key={cn} dataKey={cn} fill={COLORS[i % COLORS.length]} radius={[0, 4, 4, 0]} />
                                    ))}
                                  </BarChart>
                                </ResponsiveContainer>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* ═══ LEARNER VIEW ═══ */}
                {surveyView === "learner" && (
                  <>
                    {/* Likert tiles for learner view */}
                    {likertItems.length > 0 && (
                      <div data-testid="survey-learner-likert-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Likert Scale Items</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {likertItems.map((si) => {
                            const labels = si.likert_labels || {};
                            const scaleMin = si.likert_scale_min ?? 0;
                            const scaleMax = si.likert_scale_max ?? 5;
                            const allVals = dataRows.map((l) => l.item_scores?.[si.id]?.value).filter((v) => v != null);
                            const grandMean = allVals.length > 0 ? (allVals.reduce((s, v) => s + Number(v), 0) / allVals.length).toFixed(2) : "—";
                            return (
                              <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-4 space-y-2" data-testid={`survey-learner-likert-tile-${si.id}`}>
                                <p className="text-xs font-body font-medium text-white leading-snug line-clamp-2">{si.title}</p>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-2xl font-heading text-white">{grandMean}</span>
                                  <span className="text-xs text-[#6E7787] font-body">avg ({allVals.length} responses)</span>
                                </div>
                                <div className="text-[10px] text-[#6E7787] font-body flex justify-between">
                                  <span>{scaleMin}: {labels[String(scaleMin)] || ""}</span>
                                  <span>{scaleMax}: {labels[String(scaleMax)] || ""}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* MC pie (aggregate across learners) */}
                    {mcItems.length > 0 && (
                      <div data-testid="survey-learner-mc-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Multiple Choice Items</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {mcItems.map((si) => {
                            const optMap = {};
                            (si.answer_options || []).forEach((o) => { optMap[o.option_id] = o.text; });
                            const optCounts = {};
                            let total = 0;
                            dataRows.forEach((l) => {
                              const sc = l.item_scores?.[si.id];
                              (sc?.selected || []).forEach((oid) => { optCounts[oid] = (optCounts[oid] || 0) + 1; total++; });
                            });
                            const pieData = Object.entries(optCounts).map(([oid, count]) => ({
                              name: optMap[oid] || oid, value: count, pct: total > 0 ? Math.round((count / total) * 100) : 0,
                            }));
                            return (
                              <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid={`survey-learner-mc-chart-${si.id}`}>
                                <p className="text-xs font-body font-medium text-white mb-3 line-clamp-2">{si.title}</p>
                                {pieData.length === 0 ? <p className="text-xs text-[#6E7787]">No responses.</p> : (
                                  <>
                                    <ResponsiveContainer width="100%" height={180}>
                                      <PieChart>
                                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value" label={({ pct }) => `${pct}%`}>
                                          {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value} (${pieData.find(d => d.name === name)?.pct || 0}%)`, name]} contentStyle={{ backgroundColor: "#11141A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, color: "#fff" }} />
                                      </PieChart>
                                    </ResponsiveContainer>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                      {pieData.map((d, i) => (
                                        <span key={d.name} className="inline-flex items-center gap-1.5 text-[10px] text-[#A1A9B8]">
                                          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* SATA histogram (aggregate across learners) */}
                    {sataItems.length > 0 && (
                      <div data-testid="survey-learner-sata-section">
                        <h3 className="text-sm font-body font-semibold text-white mb-3">Select All That Apply Items</h3>
                        {sataItems.map((si) => {
                          const optMap = {};
                          (si.answer_options || []).forEach((o) => { optMap[o.option_id] = o.text; });
                          const allOpts = (si.answer_options || []).map((o) => o.option_id);
                          const optCounts = {};
                          allOpts.forEach((oid) => { optCounts[oid] = 0; });
                          let respondersN = 0;
                          dataRows.forEach((l) => {
                            const sc = l.item_scores?.[si.id];
                            if (sc) respondersN += 1;
                            (sc?.selected || []).forEach((oid) => { optCounts[oid] = (optCounts[oid] || 0) + 1; });
                          });
                          const chartData = allOpts.map((oid) => ({ name: optMap[oid] || oid, Count: optCounts[oid] }));
                          return (
                            <div key={si.id} className="bg-[#11141A] border border-white/10 rounded-xl p-5 mb-4" data-testid={`survey-learner-sata-chart-${si.id}`}>
                              <div className="flex items-start justify-between gap-3 mb-4">
                                <p className="text-xs font-body font-medium text-white">{si.title}</p>
                                <span className="shrink-0 text-[10px] font-body text-[#A1A9B8] bg-white/5 border border-white/10 rounded-full px-2 py-0.5" data-testid={`survey-learner-sata-n-${si.id}`}>
                                  n={respondersN}
                                </span>
                              </div>
                              {!chartData.some((d) => d.Count > 0) ? <p className="text-xs text-[#6E7787]">No responses.</p> : (
                                <ResponsiveContainer width="100%" height={Math.max(180, allOpts.length * 45)}>
                                  <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <XAxis type="number" tick={{ fill: "#6E7787", fontSize: 11 }} allowDecimals={false} />
                                    <YAxis dataKey="name" type="category" tick={{ fill: "#A1A9B8", fontSize: 10 }} width={160} />
                                    <Tooltip content={<ChartTooltipContent />} />
                                    <Bar dataKey="Count" fill={COLORS[2]} radius={[0, 4, 4, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Learner response table */}
                    <div data-testid="survey-learner-table">
                      <h3 className="text-sm font-body font-semibold text-white mb-3">Individual Responses</h3>
                      <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider sticky left-0 bg-[#11141A] cursor-pointer min-w-[180px]" onClick={() => toggleSurveySort("name")}>
                                  <span className="inline-flex items-center gap-1">Learner<ArrowUpDown className="h-3 w-3" /></span>
                                </th>
                                <th className="text-left px-3 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider min-w-[120px]">Cohort</th>
                                {allItems.map((si) => (
                                  <th key={si.id} className="text-center px-3 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer min-w-[120px]" onClick={() => toggleSurveySort(si.id)}>
                                    <span className="inline-flex items-center gap-1 justify-center">{si.title.length > 20 ? si.title.substring(0, 20) + "..." : si.title}<ArrowUpDown className="h-3 w-3" /></span>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {dataRows.map((row, idx) => (
                                <tr key={row.id || idx} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`survey-learner-row-${row.id}`}>
                                  <td className="px-4 py-3 text-sm font-body font-medium text-white sticky left-0 bg-[#11141A] whitespace-nowrap">{row.name}</td>
                                  <td className="px-3 py-3 text-xs font-body text-[#A1A9B8] whitespace-nowrap">{row.cohort_name || "—"}</td>
                                  {allItems.map((si) => {
                                    const sc = row.item_scores?.[si.id];
                                    const optMap = {};
                                    (si.answer_options || []).forEach((o) => { optMap[o.option_id] = o.text; });
                                    let display;
                                    if (!sc) {
                                      display = <span className="text-[#6E7787]">—</span>;
                                    } else if (sc.type === "likert") {
                                      display = sc.value != null ? sc.value : <span className="text-[#6E7787]">—</span>;
                                    } else if (sc.type === "mc" || sc.type === "sata") {
                                      const txt = (sc.selected || []).map((oid) => optMap[oid] || oid).join(", ");
                                      display = txt || <span className="text-[#6E7787]">—</span>;
                                    } else {
                                      display = sc.value != null ? String(sc.value) : <span className="text-[#6E7787]">—</span>;
                                    }
                                    return <td key={si.id} className="px-3 py-3 text-center text-sm font-body text-white">{display}</td>;
                                  })}
                                </tr>
                              ))}
                              {dataRows.length === 0 && (
                                <tr><td colSpan={2 + allItems.length} className="px-4 py-8 text-center text-sm text-[#6E7787] font-body">No data available</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: "Survey Items", value: allItems.length, color: "text-white" },
                    { label: surveyView === "cohort" ? "Cohorts" : "Learners", value: dataRows.length, color: "text-white" },
                    { label: "Responses", value: dataRows.reduce((sum, row) => sum + Object.keys(row.item_scores || {}).length, 0), color: "text-white" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="bg-[#11141A] border border-white/10 rounded-xl p-4 text-center">
                      <p className="text-[#6E7787] font-body text-xs mb-1">{label}</p>
                      <p className={`font-heading text-2xl ${color}`}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ════════════════════════════════════════════
          TAB 5: MIND CFI MATRIX
          A scatter plot of (Avg Confidence, Proportion Correct) per learner
          per CFI track. Reference lines at x=3 + y=0.5 split the chart into
          four quadrants: Mastery / Imposter / Dissonant / Novice. Multi-cohort
          selection renders side-by-side small-multiples. Each cohort gets its
          own centroid marker. Incomplete learners are listed but excluded.
          ════════════════════════════════════════════ */}
      {layer === "cfi" && (
        <CfiMatrixTab
          loading={cfiLoading}
          data={cfiData}
          selectedCohorts={cfiSelectedCohorts}
          setSelectedCohorts={setCfiSelectedCohorts}
          selectedTracks={cfiSelectedTracks}
          setSelectedTracks={setCfiSelectedTracks}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MIND CFI Matrix — sub-component
   ═══════════════════════════════════════════════════════════════ */

const CFI_QUADRANT_COLORS = {
  mastery: "#10B981",   // emerald — high confidence, high fluency
  imposter: "#F59E0B",  // amber — low confidence, high fluency
  dissonant: "#EF4444", // red — high confidence, low fluency
  novice: "#6366F1",    // indigo — low confidence, low fluency
};

const CFI_QUADRANT_LABELS = {
  mastery: "Mastery",
  imposter: "Imposter",
  dissonant: "Dissonant",
  novice: "Novice",
};

function CfiMatrixTab({ loading, data, selectedCohorts, setSelectedCohorts, selectedTracks, setSelectedTracks }) {
  const cohorts = data.cohorts || [];
  const tracks = data.tracks || [];
  const learners = data.learners || [];
  const rollups = data.rollups || {};

  const toggleCohort = (id) => {
    setSelectedCohorts((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]);
  };
  const toggleTrack = (id) => {
    setSelectedTracks((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--primary-color)" }} /></div>;
  }
  if (!cohorts.length && !learners.length) {
    return (
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-10 text-center" data-testid="cfi-empty">
        <Brain className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--admin-text)", opacity: 0.4 }} />
        <p className="text-base font-body" style={{ color: "var(--admin-text)" }}>No CFI deployments found.</p>
        <p className="text-sm font-body mt-1" style={{ color: "var(--admin-text)", opacity: 0.6 }}>
          Build a LEXP with CFI Mode enabled, attach a CFI MC item, and assign the parent track to a cohort.
        </p>
      </div>
    );
  }

  // Filter learners + rollups by current cohort/track selection (server already
  // filtered, but keeping the visible set in sync after deselection in UI).
  const visibleCohorts = selectedCohorts.length ? cohorts.filter((c) => selectedCohorts.includes(c.id)) : cohorts;
  const visibleTracks = selectedTracks.length ? tracks.filter((t) => selectedTracks.includes(t.track_id)) : tracks;
  const visibleCohortIds = new Set(visibleCohorts.map((c) => c.id));
  const visibleTrackIds = new Set(visibleTracks.map((t) => t.track_id));
  const visibleLearners = learners.filter((l) => visibleCohortIds.has(l.cohort_id) && visibleTrackIds.has(l.track_id));
  const completePoints = visibleLearners.filter((l) => l.completion_status === "complete");
  const incompletePoints = visibleLearners.filter((l) => l.completion_status === "incomplete");

  // Per-cohort point sets for either single-pane or side-by-side rendering.
  const sideBySide = visibleCohorts.length > 1;
  const cohortGroups = visibleCohorts.map((c) => {
    const pts = completePoints.filter((p) => p.cohort_id === c.id);
    const cohortRoll = rollups[c.id] || {};
    const trackRolls = Object.entries(cohortRoll).filter(([tid]) => visibleTrackIds.has(tid));
    const totalComplete = trackRolls.reduce((s, [, r]) => s + (r.n_complete || 0), 0);
    const totalIncomplete = trackRolls.reduce((s, [, r]) => s + (r.n_incomplete || 0), 0);
    const byQuad = { mastery: 0, imposter: 0, dissonant: 0, novice: 0 };
    trackRolls.forEach(([, r]) => {
      Object.entries(r.by_quadrant || {}).forEach(([q, n]) => { byQuad[q] = (byQuad[q] || 0) + n; });
    });
    // Combined centroid across selected tracks (weighted by n_complete)
    let centroid = null;
    const weighted = trackRolls
      .map(([, r]) => r.centroid && r.n_complete ? { ...r.centroid, w: r.n_complete } : null)
      .filter(Boolean);
    if (weighted.length) {
      const W = weighted.reduce((s, c) => s + c.w, 0);
      centroid = {
        x: weighted.reduce((s, c) => s + c.x * c.w, 0) / W,
        y: weighted.reduce((s, c) => s + c.y * c.w, 0) / W,
      };
    }
    return { cohort: c, points: pts, byQuad, centroid, totalComplete, totalIncomplete };
  });

  return (
    <div className="space-y-5" data-testid="cfi-matrix-tab">
      {/* Filter bar */}
      <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 space-y-3" data-testid="cfi-filter-bar">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs font-body mb-1.5" style={{ color: "var(--admin-text)", opacity: 0.6 }}>Cohorts</p>
            <div className="flex flex-wrap gap-1.5" data-testid="cfi-cohort-chips">
              {cohorts.map((c) => {
                const active = selectedCohorts.includes(c.id);
                return (
                  <button key={c.id} onClick={() => toggleCohort(c.id)} data-testid={`cfi-cohort-chip-${c.id}`}
                    className="px-2.5 py-1 text-xs font-body rounded-md border transition-colors"
                    style={{
                      borderColor: active ? "var(--primary-color, #4A90D9)" : "var(--admin-container-border)",
                      backgroundColor: active ? "color-mix(in srgb, var(--primary-color, #4A90D9) 18%, transparent)" : "transparent",
                      color: "var(--admin-text)",
                    }}
                  >{c.name || "Unnamed"}</button>
                );
              })}
              {!cohorts.length && <span className="text-xs font-body" style={{ color: "var(--admin-text)", opacity: 0.5 }}>No cohorts.</span>}
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs font-body mb-1.5" style={{ color: "var(--admin-text)", opacity: 0.6 }}>CFI Tracks</p>
            <div className="flex flex-wrap gap-1.5" data-testid="cfi-track-chips">
              {tracks.map((t) => {
                const active = selectedTracks.includes(t.track_id);
                return (
                  <button key={t.track_id} onClick={() => toggleTrack(t.track_id)} data-testid={`cfi-track-chip-${t.track_id}`}
                    className="px-2.5 py-1 text-xs font-body rounded-md border transition-colors"
                    style={{
                      borderColor: active ? "var(--primary-color, #4A90D9)" : "var(--admin-container-border)",
                      backgroundColor: active ? "color-mix(in srgb, var(--primary-color, #4A90D9) 18%, transparent)" : "transparent",
                      color: "var(--admin-text)",
                    }}
                  >{t.title || "Unnamed track"}</button>
                );
              })}
              {!tracks.length && <span className="text-xs font-body" style={{ color: "var(--admin-text)", opacity: 0.5 }}>No CFI tracks.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Aggregate quadrant tiles (across all visible cohorts + tracks) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cfi-quadrant-tiles">
        {(["mastery", "imposter", "dissonant", "novice"]).map((q) => {
          const count = cohortGroups.reduce((s, g) => s + (g.byQuad[q] || 0), 0);
          const total = cohortGroups.reduce((s, g) => s + g.totalComplete, 0) || 0;
          const pct = total ? Math.round((count / total) * 100) : 0;
          return (
            <div key={q} className="bg-[#11141A] border border-white/10 rounded-xl p-4" style={{ borderLeftWidth: 4, borderLeftColor: CFI_QUADRANT_COLORS[q] }} data-testid={`cfi-quadrant-tile-${q}`}>
              <p className="text-xs font-body uppercase tracking-wide" style={{ color: CFI_QUADRANT_COLORS[q] }}>{CFI_QUADRANT_LABELS[q]}</p>
              <p className="font-heading text-3xl mt-1" style={{ color: "var(--admin-text)" }}>{count}</p>
              <p className="text-xs font-body mt-1" style={{ color: "var(--admin-text)", opacity: 0.6 }}>{pct}% of completing learners</p>
            </div>
          );
        })}
      </div>

      {/* Scatter — single chart or side-by-side small-multiples */}
      <div className={sideBySide ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : ""}>
        {(sideBySide ? cohortGroups : [{ cohort: null, points: completePoints, centroid: aggregateCentroid(cohortGroups), byQuad: aggregateByQuad(cohortGroups), totalComplete: completePoints.length, totalIncomplete: incompletePoints.length }]).map((g, idx) => (
          <CfiScatterPanel key={(g.cohort && g.cohort.id) || `agg-${idx}`} group={g} sideBySide={sideBySide} />
        ))}
      </div>

      {/* Incomplete list */}
      {incompletePoints.length > 0 && (
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid="cfi-incomplete-list">
          <p className="text-sm font-body font-semibold" style={{ color: "var(--admin-text)" }}>Incomplete (excluded from rollup)</p>
          <p className="text-xs font-body mb-3" style={{ color: "var(--admin-text)", opacity: 0.6 }}>
            {incompletePoints.length} learner{incompletePoints.length !== 1 ? "s" : ""} have not completed every paired item across the selected CFI track{visibleTracks.length !== 1 ? "s" : ""}.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {incompletePoints.slice(0, 30).map((p) => (
              <div key={`${p.learner_id}-${p.track_id}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-[#0A0C10] border border-white/5">
                <span className="text-xs font-body truncate" style={{ color: "var(--admin-text)" }}>{p.name}</span>
                <span className="text-xs font-body shrink-0" style={{ color: "var(--admin-text)", opacity: 0.6 }}>{p.mc_answered + p.likert_answered}/{p.mc_total + p.likert_total}</span>
              </div>
            ))}
            {incompletePoints.length > 30 && (
              <div className="px-3 py-2 text-xs font-body" style={{ color: "var(--admin-text)", opacity: 0.5 }}>+{incompletePoints.length - 30} more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function aggregateCentroid(groups) {
  const weighted = groups.map((g) => g.centroid && g.totalComplete ? { ...g.centroid, w: g.totalComplete } : null).filter(Boolean);
  if (!weighted.length) return null;
  const W = weighted.reduce((s, c) => s + c.w, 0);
  return {
    x: weighted.reduce((s, c) => s + c.x * c.w, 0) / W,
    y: weighted.reduce((s, c) => s + c.y * c.w, 0) / W,
  };
}

function aggregateByQuad(groups) {
  const out = { mastery: 0, imposter: 0, dissonant: 0, novice: 0 };
  groups.forEach((g) => Object.entries(g.byQuad || {}).forEach(([q, n]) => { out[q] = (out[q] || 0) + n; }));
  return out;
}

function CfiScatterPanel({ group, sideBySide }) {
  const { cohort, points, totalComplete, totalIncomplete } = group;
  // Group points by quadrant so each quadrant is its own Scatter series with
  // a distinct color. Recharts requires numeric x/y on each datum.
  const byQuadrantSeries = ["mastery", "imposter", "dissonant", "novice"].map((q) => ({
    quadrant: q,
    color: CFI_QUADRANT_COLORS[q],
    data: points
      .filter((p) => p.quadrant === q)
      .map((p) => ({ x: p.avg_confidence, y: p.proportion_correct, name: p.name })),
  }));

  return (
    <div className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid={`cfi-scatter-panel-${(cohort && cohort.id) || "agg"}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-body font-semibold" style={{ color: "var(--admin-text)" }}>{cohort ? cohort.name : "Selected Cohorts"}</p>
          <p className="text-xs font-body" style={{ color: "var(--admin-text)", opacity: 0.6 }}>
            {totalComplete} complete · {totalIncomplete} incomplete
          </p>
        </div>
      </div>
      {/* Plot wrapper. Quadrant labels render INSIDE the plot via Recharts
          ReferenceLine labels (transparent stroke). Each label is anchored to
          its quadrant corner so the layout follows the data area, not the
          card padding. */}
      <ResponsiveContainer width="100%" height={sideBySide ? 320 : 460}>
        <ScatterChart margin={{ top: 30, right: 30, bottom: 42, left: 42 }}>
          <CartesianGrid stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" />
          <XAxis type="number" dataKey="x" name="Confidence" domain={[1, 5]} ticks={[1, 2, 3, 4, 5]}
            tick={{ fontSize: 12, fill: "#000" }}
            label={{ value: "Confidence", position: "insideBottom", offset: -18, style: { fontSize: 13, fontWeight: 700, fill: "#000" } }} />
          <YAxis type="number" dataKey="y" name="Fluency" domain={[0, 1]} ticks={[0, 0.25, 0.5, 0.75, 1]}
            tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
            tick={{ fontSize: 12, fill: "#000" }}
            label={{ value: "Fluency", angle: -90, position: "insideLeft", style: { fontSize: 13, fontWeight: 700, textAnchor: "middle", fill: "#000" } }} />
          <ZAxis range={[100, 100]} />
          <Tooltip cursor={{ strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const p = payload[0].payload;
              return (
                <div className="rounded-md px-3 py-2 text-xs font-body shadow-md"
                  style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(0,0,0,0.15)", color: "#000000" }}>
                  <p className="font-semibold" style={{ color: "#000000" }}>{p.name}</p>
                  <p style={{ color: "#000000" }}>Confidence: {p.x?.toFixed(2)}</p>
                  <p style={{ color: "#000000" }}>Fluency: {(p.y * 100).toFixed(0)}%</p>
                </div>
              );
            }}
          />
          {/* Midpoint reference lines — solid, clearly visible across themes.
              x=3 splits Confidence; y=0.5 splits Fluency. Together they carve
              the plot into the four CFI quadrants. The colored dots + the
              quadrant tiles above the plot already encode quadrant
              membership, so no in-plot quadrant labels are rendered. */}
          <ReferenceLine x={3} stroke="rgba(0,0,0,0.45)" strokeWidth={1.5} ifOverflow="extendDomain" />
          <ReferenceLine y={0.5} stroke="rgba(0,0,0,0.45)" strokeWidth={1.5} ifOverflow="extendDomain" />
          {byQuadrantSeries.map((s) => (
            <Scatter key={s.quadrant} name={CFI_QUADRANT_LABELS[s.quadrant]} data={s.data} fill={s.color} fillOpacity={0.9} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
